import { useState, useRef, ChangeEvent, ReactNode, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useTranslation } from '@/hooks/useTranslation'
import {
	Bug,
	Image as ImageIcon,
	X,
	Loader2,
	MessageSquare,
	Star,
	ArrowLeft,
	ArrowRight,
	Check,
	ChevronUp,
	Clock,
	Search,
	Plus,
	AlertCircle,
	LightbulbIcon,
	Frown,
	Meh,
	Smile,
	Laugh,
	Send,
	MessageCircle,
	Mail,
	Download,
	Expand,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
	submitBugReport,
	submitContactMessage,
	submitFeatureRequest,
	fetchFeatureRequests,
	upvoteFeature,
	checkUpvoteStatus,
	submitReview,
	requestVerificationCode,
	submitVerifiedReview,
	type FeatureRequest,
} from '@/lib/services/feedback'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { useSidebar } from '@/components/ui/sidebar'
import { getApiBaseUrl } from '@/lib/api'
import { supabase } from '@/integrations/supabase/client'
import {
	Dialog,
	DialogContent,
	DialogClose,
	DialogTitle,
} from '@/components/ui/dialog'
import { VisuallyHidden } from '@/components/ui/visually-hidden'
import { AgentAvailabilityIndicator } from '@/components/widget/AgentAvailabilityIndicator'
import { AgentProfileCard } from '@/components/widget/AgentProfileCard'

// Define the structure for the widget configuration passed from the builder
export interface WidgetConfig {
	id?: string
	team_id?: string
	program_id: string
	name?: string
	header_title?: string
	position?:
		| 'middle-left'
		| 'middle-right'
		| 'bottom-left'
		| 'bottom-center'
		| 'bottom-right'
	primaryColor?: string
	gradientEndColor?: string | null
	backgroundColor?: string
	textColor?: string
	// Module Enables
	enable_survey?: boolean
	enable_bug_reporting?: boolean
	enable_contact_messages?: boolean
	contact_bg?: string
	contact_icon?: string
	enable_feature_requests?: boolean
	enable_reviews?: boolean
	enable_live_chat?: boolean
	// Primary Feedback Config
	primary_feedback_type?: 'nps' | 'csat' | 'custom' | 'testimonial'
	question?: string
	customQuestions?: {
		id: string
		type: 'text' | 'email' | 'multiple_choice' | 'scale'
		question: string
		required?: boolean
		options?: string[]
		order_index?: number
		min_scale?: number
		max_scale?: number
	}[]
	buttonText?: string
	// Thank You Message
	thankYou?: { title: string; message: string }
	// Feedback Button Config
	feedbackButton?: {
		show: boolean
		text: string
		color: string
		textColor: string
	}
	widgetTriggerSize?: 's' | 'm' | 'l'
	widgetTriggerIcon?: string
	showBranding?: boolean
	brandingStyle?: {
		borderColor?: string
	}
	// Header Display
	showHeader?: boolean
	// Menu Item Colors
	menuItemColors?: {
		feedback_bg?: string
		feedback_icon?: string
		review_bg?: string
		review_icon?: string
		bug_bg?: string
		bug_icon?: string
		contact_bg?: string
		contact_icon?: string
		feature_bg?: string
		feature_icon?: string
	}
	// Menu Item Style - Enhanced version
	menuItemStyle?: {
		layout?: 'default' | 'separate'
		background?: string
		textColor?: string
		iconBackgroundColor?: string
		iconColor?: string
		showDescriptions?: boolean
		useBorder?: boolean
		borderColor?: string
		centerAlign?: boolean
		verticalAlign?: boolean
	}
	// Animation settings
	animation?: {
		header?: 'none' | 'subtle-gradient-shift'
		backgroundEffects?: boolean
	}
	// Glassmorphism settings
	glassmorphism?: {
		enabled?: boolean
		backdropBlur?: string
		borderColor?: string
	}
	// Layout configuration for theme-specific spacing and styling
	layout?: {
		spacing?: 'compact' | 'default' | 'spacious'
		menuPadding?: string
		itemSpacing?: string
		menuColumns?: 1 | 2
		containerBorder?: boolean
		headerPadding?: string
		headerBackground?: string
		headerTextColor?: string
	}
	moduleNavigation?: {
		enabled?: boolean
		mode?: 'bottom-tabs'
		defaultToFirstModule?: boolean
		modules?: Array<
			| 'feedback'
			| 'review'
			| 'mailbox'
			| 'bug-report'
			| 'feature-request'
			| 'roadmap'
			| 'live-chat'
		>
	}
	// Trigger configuration
	trigger?: {
		type?: 'immediate' | 'delay' | 'scroll' | 'exit' | 'manual'
		delay?: number
		scrollPercentage?: number
	}
	// Language configuration
	language?: string
}

export interface WidgetPreviewProps {
	config: WidgetConfig
	showManualTrigger?: boolean // When true, shows the widget even if trigger type is manual
}

// Define possible views within the widget
type ViewMode =
	| 'mainMenu'
	| 'bugForm'
	| 'contactForm'
	| 'featureList'
	| 'featureForm'
	| 'surveyStep' // For NPS, CSAT, Custom Survey steps
	| 'testimonialForm' // If enable_reviews is separate or primary_feedback_type is testimonial
	| 'reviewForm' // Dedicated simple review flow (controlled by enable_reviews)
	| 'thankYou'
	| 'chatInit' // Live chat initiation form
	| 'chatView' // Live chat conversation view

type BottomModuleId =
	| 'feedback'
	| 'review'
	| 'mailbox'
	| 'bug-report'
	| 'feature-request'
	| 'roadmap'
	| 'live-chat'

// Animation variants
const containerVariants = {
	hidden: { opacity: 0, scale: 0.95 },
	visible: {
		opacity: 1,
		scale: 1,
		transition: { duration: 0.2 },
	},
	exit: {
		opacity: 0,
		scale: 0.95,
		transition: { duration: 0.15 },
	},
}

const contentVariants = {
	enter: (direction: number) => ({
		x: direction > 0 ? 100 : -100,
		opacity: 0,
	}),
	center: {
		x: 0,
		opacity: 1,
	},
	exit: (direction: number) => ({
		x: direction < 0 ? 100 : -100,
		opacity: 0,
	}),
}

// Translations for widget interface
const translations: Record<string, Record<string, string>> = {
	en: {
		menu_title: 'How can we help you?',
		feedback: 'Feedback',
		feedback_desc: 'Share your thoughts',
		bug_report: 'Report a Bug',
		contact_message: 'Contact Us',
		bug_desc: 'Something not working?',
		contact_desc: 'Send us a message',
		feature_request: 'Feature Request',
		feature_desc: 'Suggest improvements',
		reviews: 'Leave a Review',
		reviews_desc: 'Share your experience',
		live_chat: 'Live Chat',
		live_chat_desc: 'Talk to us',
		submit: 'Submit',
		back: 'Back',
		next: 'Next',
		skip: 'Skip',
		close: 'Close',
		thank_you: 'Thank you!',
		feedback_received: 'We appreciate your feedback.',
		bug_title: 'Report a Bug',
		contact_title: 'Contact Us',
		bug_description: 'Describe the issue',
		contact_description: 'Your Message',
		bug_placeholder: 'Please describe what went wrong...',
		contact_placeholder: 'How can we help you?',
		describe_issue: 'Describe the issue (min 10 chars)',
		what_trying_to_do: 'What were you trying to do?...',
		screenshot_optional: 'Screenshot (optional)',
		attach_screenshot: 'Attach Screenshot',
		your_name: 'Your Name',
		your_email: 'Your Email',
		optional: 'Optional',
		your_name_optional: 'Your Name (Optional)',
		your_email_optional: 'Your Email (Optional)',
		feature_title: 'Feature Requests',
		search_features: 'Search features...',
		suggest_feature: 'Suggest New Feature',
		suggest_a_feature: 'Suggest a Feature',
		upvote: 'Upvote',
		upvoted: 'Upvoted',
		share_experience: 'Share your experience (min 10 chars)',
		nps_question: 'How likely are you to recommend us?',
		csat_question: 'How satisfied were you?',
		overall_rating: 'Overall rating',
		overall_rating_optional: 'Overall rating (optional)',
		no_feature_requests: 'No feature requests yet',
		be_first_suggest: 'Be the first to suggest a feature!',
		title: 'Title',
		description: 'Description',
		feature_idea_brief: 'Brief description of your feature idea',
		feature_idea_detail: 'Describe your feature idea in detail...',
		name: 'Name',
		email: 'Email',
		required_field: 'required',
		social_media_link: 'Social Media Link (optional)',
		i_agree_feedback: 'I agree that my feedback may be used publicly for marketing or testimonials.',
		continue: 'Continue',
		providing_email_help: 'Providing your email helps us follow up if we have questions.',
		attach_screenshot_optional: 'Attach Screenshot (Optional, Max 5MB)',
		share_your_feedback: 'Share your feedback',
		email_required_verification: 'Email (required for verification)',
		name_optional: 'Name (optional)',
		full_name_or_company: 'Full Name or Company Name',
		tell_us_what_you_think: 'Tell us what you think...',
		search_feature_requests: 'Search feature requests...',
		start_conversation: 'Start a Conversation',
		work_email: 'Work email',
		email_follow_up: 'We\'ll use your email to follow up if you leave the page.',
		hi_greeting: 'Hi',
		chat_name: 'Name',
		submit_feedback: 'Submit Feedback',
		start_chat: 'Start chat',
		not_likely: 'Not Likely',
		very_likely: 'Very Likely',
		your_answer: 'Your answer...',
		attach_screenshot_btn: 'Attach Screenshot',
		offline: 'Offline',
		leave_us_message: 'Leave us a message',
		team_offline: 'Our team is currently offline. We\'ll get back to you as soon as possible.',
		say_hi: 'Say hi, we\'ll reply here.',
		agent_typing: 'Agent is typing',
		email_placeholder: 'your.email@example.com',
	},
	fr: {
		menu_title: 'Comment pouvons-nous vous aider ?',
		feedback: 'Commentaires',
		feedback_desc: 'Partagez vos pensées',
		bug_report: 'Signaler un Bug',
		bug_desc: 'Quelque chose ne fonctionne pas ?',
		feature_request: 'Demande de Fonctionnalité',
		feature_desc: 'Suggérer des améliorations',
		reviews: 'Laisser un Avis',
		reviews_desc: 'Partagez votre expérience',
		live_chat: 'Chat en Direct',
		live_chat_desc: 'Parlez-nous',
		submit: 'Envoyer',
		back: 'Retour',
		next: 'Suivant',
		skip: 'Passer',
		close: 'Fermer',
		thank_you: 'Merci !',
		feedback_received: 'Nous apprécions vos commentaires.',
		bug_title: 'Signaler un Bug',
		bug_description: 'Décrivez le problème',
		bug_placeholder: 'Veuillez décrire ce qui ne fonctionne pas...',
		describe_issue: 'Décrivez le problème (min 10 caractères)',
		what_trying_to_do: 'Que tentiez-vous de faire ?...',
		screenshot_optional: 'Capture d\'écran (optionnelle)',
		attach_screenshot: 'Joindre une capture',
		your_name: 'Votre Nom',
		your_email: 'Votre Email',
		optional: 'Optionnel',
		your_name_optional: 'Votre Nom (Optionnel)',
		your_email_optional: 'Votre Email (Optionnel)',
		feature_title: 'Demandes de Fonctionnalités',
		search_features: 'Rechercher des fonctionnalités...',
		suggest_feature: 'Suggérer une Nouvelle Fonctionnalité',
		suggest_a_feature: 'Suggérer une Fonctionnalité',
		upvote: 'Voter pour',
		upvoted: 'Voté',
		share_experience: 'Partagez votre expérience (min 10 caractères)',
		nps_question: 'Quelle est la probabilité que vous nous recommandiez ?',
		csat_question: 'Êtes-vous satisfait ?',
		overall_rating: 'Note globale',
		overall_rating_optional: 'Note globale (optionnelle)',
		no_feature_requests: 'Aucune demande de fonctionnalité pour le moment',
		be_first_suggest: 'Soyez le premier à suggérer une fonctionnalité !',
		title: 'Titre',
		description: 'Description',
		feature_idea_brief: 'Brève description de votre idée',
		feature_idea_detail: 'Décrivez votre idée en détail...',
		name: 'Nom',
		email: 'Email',
		required_field: 'requis',
		social_media_link: 'Lien de réseau social (optionnel)',
		i_agree_feedback: 'J\'accepte que mes commentaires puissent être utilisés publiquement à des fins de marketing ou de témoignages.',
		continue: 'Continuer',
		providing_email_help: 'Fournir votre email nous aide à vous recontacter si nous avons des questions.',
		attach_screenshot_optional: 'Joindre une capture (Optionnel, Max 5MB)',
		share_your_feedback: 'Partagez vos commentaires',
		email_required_verification: 'Email (requis pour vérification)',
		name_optional: 'Nom (optionnel)',
		full_name_or_company: 'Nom complet ou nom de l\'entreprise',
		tell_us_what_you_think: 'Dites-nous ce que vous pensez...',
		search_feature_requests: 'Rechercher des demandes de fonctionnalités...',
		start_conversation: 'Commencer une conversation',
		work_email: 'Email professionnel',
		email_follow_up: 'Nous utiliserons votre email pour vous recontacter si vous quittez la page.',
		hi_greeting: 'Bonjour',
		chat_name: 'Nom',
		submit_feedback: 'Envoyer les commentaires',
		start_chat: 'Démarrer le chat',
		not_likely: 'Peu probable',
		very_likely: 'Très probable',
		your_answer: 'Votre réponse...',
		attach_screenshot_btn: 'Joindre une capture',
		offline: 'Hors ligne',
		leave_us_message: 'Laissez-nous un message',
		team_offline: 'Notre équipe est actuellement hors ligne. Nous vous répondrons dès que possible.',
		say_hi: 'Dites bonjour, nous répondrons ici.',
		agent_typing: 'L\'agent est en train d\'écrire',
		email_placeholder: 'votre.email@exemple.com',
	},
	de: {
		menu_title: 'Wie können wir Ihnen helfen?',
		feedback: 'Feedback',
		feedback_desc: 'Teilen Sie Ihre Gedanken',
		bug_report: 'Fehler melden',
		bug_desc: 'Funktioniert etwas nicht?',
		feature_request: 'Funktionsanfrage',
		feature_desc: 'Verbesserungen vorschlagen',
		reviews: 'Bewertung abgeben',
		reviews_desc: 'Teilen Sie Ihre Erfahrung',
		live_chat: 'Live-Chat',
		live_chat_desc: 'Mit uns sprechen',
		submit: 'Absenden',
		back: 'Zurück',
		next: 'Weiter',
		skip: 'Überspringen',
		close: 'Schließen',
		thank_you: 'Vielen Dank!',
		feedback_received: 'Wir schätzen Ihr Feedback.',
		bug_title: 'Fehler melden',
		bug_description: 'Beschreiben Sie das Problem',
		bug_placeholder: 'Bitte beschreiben Sie, was schief gelaufen ist...',
		screenshot_optional: 'Screenshot (optional)',
		attach_screenshot: 'Screenshot anhängen',
		your_name: 'Ihr Name',
		your_email: 'Ihre E-Mail',
		optional: 'Optional',
		your_name_optional: 'Ihr Name (Optional)',
		your_email_optional: 'Ihre E-Mail (Optional)',
		feature_title: 'Funktionsanfragen',
		search_features: 'Funktionen suchen...',
		suggest_feature: 'Neue Funktion vorschlagen',
		suggest_a_feature: 'Eine Funktion vorschlagen',
		upvote: 'Zustimmen',
		upvoted: 'Zugestimmt',
		share_experience: 'Teilen Sie Ihre Erfahrung (mind. 10 Zeichen)',
		nps_question: 'Wie wahrscheinlich werden Sie uns weiterempfehlen?',
		csat_question: 'Wie zufrieden waren Sie?',
		overall_rating: 'Gesamtbewertung',
		overall_rating_optional: 'Gesamtbewertung (optional)',
		no_feature_requests: 'Noch keine Funktionsanfragen',
		be_first_suggest: 'Seien Sie der Erste, der eine Funktion vorschlägt!',
		title: 'Titel',
		description: 'Beschreibung',
		feature_idea_brief: 'Kurze Beschreibung Ihrer Funktionsidee',
		feature_idea_detail: 'Beschreiben Sie Ihre Funktionsidee im Detail...',
		describe_issue: 'Beschreiben Sie das Problem (mind. 10 Zeichen)',
		what_trying_to_do: 'Was haben Sie versucht zu tun?...',
		name: 'Name',
		email: 'E-Mail',
		required_field: 'erforderlich',
		social_media_link: 'Social Media Link (optional)',
		i_agree_feedback: 'Ich bin damit einverstanden, dass mein Feedback öffentlich für Marketing oder Testimonials verwendet werden kann.',
		continue: 'Weiter',
		providing_email_help: 'Die Angabe Ihrer E-Mail hilft uns, Sie bei Fragen zu kontaktieren.',
		attach_screenshot_optional: 'Screenshot anhängen (Optional, Max 5MB)',
		share_your_feedback: 'Teilen Sie Ihr Feedback',
		email_required_verification: 'E-Mail (erforderlich für Verifizierung)',
		name_optional: 'Name (optional)',
		full_name_or_company: 'Vollständiger Name oder Firmenname',
		tell_us_what_you_think: 'Teilen Sie uns Ihre Meinung mit...',
		search_feature_requests: 'Funktionsanfragen durchsuchen...',
		start_conversation: 'Ein Gespräch beginnen',
		work_email: 'Geschäftliche E-Mail',
		email_follow_up: 'Wir verwenden Ihre E-Mail, um Sie zu kontaktieren, wenn Sie die Seite verlassen.',
		hi_greeting: 'Hallo',
		chat_name: 'Name',
		submit_feedback: 'Feedback senden',
		start_chat: 'Chat starten',
		not_likely: 'Unwahrscheinlich',
		very_likely: 'Sehr wahrscheinlich',
		your_answer: 'Ihre Antwort...',
		attach_screenshot_btn: 'Screenshot anhängen',
		offline: 'Offline',
		leave_us_message: 'Hinterlassen Sie uns eine Nachricht',
		team_offline: 'Unser Team ist derzeit offline. Wir melden uns so schnell wie möglich bei Ihnen.',
		say_hi: 'Sagen Sie Hallo, wir antworten hier.',
		agent_typing: 'Agent schreibt',
		email_placeholder: 'ihre.email@beispiel.de',
	},
	es: {
		menu_title: '¿Cómo podemos ayudarte?',
		feedback: 'Comentarios',
		feedback_desc: 'Comparte tus ideas',
		bug_report: 'Reportar un Error',
		bug_desc: '¿Algo no funciona?',
		feature_request: 'Solicitud de Función',
		feature_desc: 'Sugerir mejoras',
		reviews: 'Dejar una Reseña',
		reviews_desc: 'Comparte tu experiencia',
		live_chat: 'Chat en Vivo',
		live_chat_desc: 'Habla con nosotros',
		submit: 'Enviar',
		back: 'Atrás',
		next: 'Siguiente',
		skip: 'Saltar',
		close: 'Cerrar',
		thank_you: '¡Gracias!',
		feedback_received: 'Apreciamos tus comentarios.',
		bug_title: 'Reportar un Error',
		bug_description: 'Describe el problema',
		bug_placeholder: 'Por favor describe qué salió mal...',
		screenshot_optional: 'Captura de pantalla (opcional)',
		attach_screenshot: 'Adjuntar captura',
		your_name: 'Tu Nombre',
		your_email: 'Tu Email',
		optional: 'Opcional',
		your_name_optional: 'Tu Nombre (Opcional)',
		your_email_optional: 'Tu Email (Opcional)',
		feature_title: 'Solicitudes de Funciones',
		search_features: 'Buscar funciones...',
		suggest_feature: 'Sugerir Nueva Función',
		suggest_a_feature: 'Sugerir una Función',
		upvote: 'Votar a favor',
		upvoted: 'Votado',
		share_experience: 'Comparte tu experiencia (mín 10 caracteres)',
		nps_question: '¿Qué tan probable es que nos recomiendes?',
		csat_question: '¿Qué tan satisfecho estuviste?',
		overall_rating: 'Calificación general',
		overall_rating_optional: 'Calificación general (opcional)',
		no_feature_requests: 'Aún no hay solicitudes de funciones',
		be_first_suggest: '¡Sé el primero en sugerir una función!',
		title: 'Título',
		description: 'Descripción',
		feature_idea_brief: 'Breve descripción de tu idea',
		feature_idea_detail: 'Describe tu idea en detalle...',
		describe_issue: 'Describe el problema (mín 10 caracteres)',
		what_trying_to_do: '¿Qué intentabas hacer?...',
		name: 'Nombre',
		email: 'Email',
		required_field: 'requerido',
		social_media_link: 'Enlace de redes sociales (opcional)',
		i_agree_feedback: 'Acepto que mis comentarios puedan ser utilizados públicamente para marketing o testimonios.',
		continue: 'Continuar',
		providing_email_help: 'Proporcionar tu email nos ayuda a contactarte si tenemos preguntas.',
		attach_screenshot_optional: 'Adjuntar captura (Opcional, Máx 5MB)',
		share_your_feedback: 'Comparte tus comentarios',
		email_required_verification: 'Email (requerido para verificación)',
		name_optional: 'Nombre (opcional)',
		full_name_or_company: 'Nombre completo o nombre de la empresa',
		tell_us_what_you_think: 'Cuéntanos qué piensas...',
		search_feature_requests: 'Buscar solicitudes de funciones...',
		start_conversation: 'Iniciar una conversación',
		work_email: 'Email de trabajo',
		email_follow_up: 'Usaremos tu email para contactarte si abandonas la página.',
		hi_greeting: 'Hola',
		chat_name: 'Nombre',
		submit_feedback: 'Enviar comentarios',
		start_chat: 'Iniciar chat',
		not_likely: 'Poco probable',
		very_likely: 'Muy probable',
		your_answer: 'Tu respuesta...',
		attach_screenshot_btn: 'Adjuntar captura',
		offline: 'Fuera de línea',
		leave_us_message: 'Déjanos un mensaje',
		team_offline: 'Nuestro equipo está actualmente fuera de línea. Te responderemos lo antes posible.',
		say_hi: 'Saluda, responderemos aquí.',
		agent_typing: 'El agente está escribiendo',
		email_placeholder: 'tu.email@ejemplo.com',
	},
	zh: {
		menu_title: '我们如何帮助您？',
		feedback: '反馈',
		feedback_desc: '分享您的想法',
		bug_report: '报告错误',
		bug_desc: '有什么不工作吗？',
		feature_request: '功能请求',
		feature_desc: '建议改进',
		reviews: '留下评论',
		reviews_desc: '分享您的经验',
		live_chat: '在线聊天',
		live_chat_desc: '与我们交谈',
		submit: '提交',
		back: '返回',
		next: '下一步',
		skip: '跳过',
		close: '关闭',
		thank_you: '谢谢！',
		feedback_received: '我们感谢您的反馈。',
		bug_title: '报告错误',
		bug_description: '描述问题',
		bug_placeholder: '请描述出了什么问题...',
		screenshot_optional: '截图（可选）',
		attach_screenshot: '附加截图',
		your_name: '您的名字',
		your_email: '您的电子邮件',
		optional: '可选',
		your_name_optional: '您的名字（可选）',
		your_email_optional: '您的电子邮件（可选）',
		feature_title: '功能请求',
		search_features: '搜索功能...',
		suggest_feature: '建议新功能',
		suggest_a_feature: '建议功能',
		upvote: '赞成',
		upvoted: '已赞成',
		share_experience: '分享您的经验（至少10个字符）',
		nps_question: '您推荐我们的可能性有多大？',
		csat_question: '您满意吗？',
		overall_rating: '总体评分',
		overall_rating_optional: '总体评分（可选）',
		no_feature_requests: '暂无功能请求',
		be_first_suggest: '成为第一个建议功能的人！',
		title: '标题',
		description: '描述',
		feature_idea_brief: '您的功能想法的简要描述',
		feature_idea_detail: '详细描述您的功能想法...',
		describe_issue: '描述问题（至少10个字符）',
		what_trying_to_do: '您试图做什么？...',
		name: '姓名',
		email: '电子邮件',
		required_field: '必填',
		social_media_link: '社交媒体链接（可选）',
		i_agree_feedback: '我同意我的反馈可以公开用于营销或推荐。',
		continue: '继续',
		providing_email_help: '提供您的电子邮件有助于我们在有问题时与您联系。',
		attach_screenshot_optional: '附加截图（可选，最大5MB）',
		share_your_feedback: '分享您的反馈',
		email_required_verification: '电子邮件（验证必需）',
		name_optional: '姓名（可选）',
		full_name_or_company: '全名或公司名称',
		tell_us_what_you_think: '告诉我们您的想法...',
		search_feature_requests: '搜索功能请求...',
		start_conversation: '开始对话',
		work_email: '工作邮箱',
		email_follow_up: '如果您离开页面，我们将使用您的电子邮件与您联系。',
		hi_greeting: '您好',
		chat_name: '姓名',
		submit_feedback: '提交反馈',
		start_chat: '开始聊天',
		not_likely: '不太可能',
		very_likely: '非常可能',
		your_answer: '您的回答...',
		attach_screenshot_btn: '附加截图',
		offline: '离线',
		leave_us_message: '给我们留言',
		team_offline: '我们的团队目前处于离线状态。我们会尽快回复您。',
		say_hi: '打个招呼, 我们会在这里回复。',
		agent_typing: '客服正在输入',
		email_placeholder: '您的邮箱@例子.com',
	},
	km: {
		menu_title: 'តើយើងអាចជួយអ្នកយ៉ាងដូចម្តេច?',
		feedback: 'មតិកែលម្អ',
		feedback_desc: 'ចែករំលែកគំនិតរបស់អ្នក',
		bug_report: 'រាយការណ៍កំហុស',
		bug_desc: 'មានអ្វីមិនដំណើរការទេ?',
		feature_request: 'សំណើមុខងារ',
		feature_desc: 'ស្នើការកែលម្អ',
		reviews: 'ទុកមតិយោបល់',
		reviews_desc: 'ចែករំលែកបទពិសោធន៍របស់អ្នក',
		live_chat: 'ជជែកផ្ទាល់',
		live_chat_desc: 'និយាយជាមួយយើង',
		submit: 'ដាក់ស្នើ',
		back: 'ត្រឡប់',
		next: 'បន្ទាប់',
		skip: 'រំលង',
		close: 'បិទ',
		thank_you: 'អរគុណ!',
		feedback_received: 'យើងកោតសរសើរចំពោះមតិរបស់អ្នក។',
		bug_title: 'រាយការណ៍កំហុស',
		bug_description: 'ពិពណ៌នាអំពីបញ្ហា',
		bug_placeholder: 'សូមពិពណ៌នាអំពីអ្វីដែលមានបញ្ហា...',
		screenshot_optional: 'រូបថតអេក្រង់ (ស្រេចចិត្ត)',
		attach_screenshot: 'ភ្ជាប់រូបថត',
		your_name: 'ឈ្មោះរបស់អ្នក',
		your_email: 'អ៊ីមែលរបស់អ្នក',
		optional: 'ស្រេចចិត្ត',
		your_name_optional: 'ឈ្មោះរបស់អ្នក (ស្រេចចិត្ត)',
		your_email_optional: 'អ៊ីមែលរបស់អ្នក (ស្រេចចិត្ត)',
		feature_title: 'សំណើមុខងារ',
		search_features: 'ស្វែងរកមុខងារ...',
		suggest_feature: 'ស្នើមុខងារថ្មី',
		suggest_a_feature: 'ស្នើមុខងារ',
		upvote: 'បោះឆ្នោតគាំទ្រ',
		upvoted: 'បានបោះឆ្នោត',
		share_experience: 'ចែករំលែកបទពិសោធន៍របស់អ្នក (យ៉ាងតិច 10 តួអក្សរ)',
		nps_question: 'តើលោកអ្នកនឹងណែនាំយើងខ្ញុំដោយទំនុកចិត្តប៉ុណ្ណា?',
		csat_question: 'តើអ្នកពេញចិត្ដទេ?',
		overall_rating: 'ការវាយតម្លៃទូទៅ',
		overall_rating_optional: 'ការវាយតម្លៃទូទៅ (ស្រេចចិត្ត)',
		no_feature_requests: 'មិនទាន់មានសំណើមុខងារនៅឡើយទេ',
		be_first_suggest: 'ក្លាយជាអ្នកដំបូងដែលស្នើមុខងារ!',
		title: 'ចំណងជើង',
		description: 'ការពិពណ៌នា',
		feature_idea_brief: 'ការពិពណ៌នាខ្លីនៃគំនិតមុខងាររបស់អ្នក',
		feature_idea_detail: 'ពិពណ៌នាគំនិតមុខងាររបស់អ្នកលម្អិត...',
		describe_issue: 'ពិពណ៌នាបញ្ហា (យ៉ាងតិច 10 តួអក្សរ)',
		what_trying_to_do: 'តើអ្នកកំពុងព្យាយាមធ្វើអ្វី?...',
		name: 'ឈ្មោះ',
		email: 'អ៊ីមែល',
		required_field: 'ចាំបាច់',
		social_media_link: 'តំណភ្ជាប់បណ្តាញសង្គម (ស្រេចចិត្ត)',
		i_agree_feedback: 'ខ្ញុំយល់ព្រមឱ្យមតិរបស់ខ្ញុំត្រូវបានប្រើប្រាស់ជាសាធារណៈសម្រាប់ទីផ្សារ ឬសក្ខីកម្ម។',
		continue: 'បន្ត',
		providing_email_help: 'ការផ្តល់អ៊ីមែលរបស់អ្នកជួយយើងទាក់ទងបានប្រសិនបើមានសំណួរ។',
		attach_screenshot_optional: 'ភ្ជាប់រូបថត (ស្រេចចិត្ត, អតិបរមា 5MB)',
		share_your_feedback: 'ចែករំលែកមតិរបស់អ្នក',
		email_required_verification: 'អ៊ីមែល (ចាំបាច់សម្រាប់ផ្ទៀងផ្ទាត់)',
		name_optional: 'ឈ្មោះ (ស្រេចចិត្ត)',
		full_name_or_company: 'ឈ្មោះពេញ ឬឈ្មោះក្រុមហ៊ុន',
		tell_us_what_you_think: 'ប្រាប់យើងពីគំនិតរបស់អ្នក...',
		search_feature_requests: 'ស្វែងរកសំណើមុខងារ...',
		start_conversation: 'ចាប់ផ្តើមការសន្ទនា',
		work_email: 'អ៊ីមែលធ្វើការ',
		email_follow_up: 'យើងនឹងប្រើអ៊ីមែលរបស់អ្នកដើម្បីទាក់ទងប្រសិនបើអ្នកចាកចេញពីទំព័រ។',
		hi_greeting: 'សួស្តី',
		chat_name: 'ឈ្មោះ',
		submit_feedback: 'ផ្ញើមតិកែលម្អ',
		start_chat: 'ចាប់ផ្តើមជជែក',
		not_likely: 'មិនសូវអាច',
		very_likely: 'ទំនងខ្លាំង',
		your_answer: 'ចម្លើយរបស់អ្នក...',
		attach_screenshot_btn: 'ភ្ជាប់រូបថត',
		offline: 'នៅក្រៅបណ្តាញ',
		leave_us_message: 'ទុកសារមួយ',
		team_offline: 'ក្រុមរបស់យើងបច្ចុប្បន្ននៅក្រៅបណ្តាញ។ យើងនឹងឆ្លើយតបឱ្យអ្នកឱ្យបានឆាប់តាមដែលអាចធ្វើបាន។',
		say_hi: 'សួរសុខទុក្ខ, យើងនឹងឆ្លើយតបនៅទីនេះ។',
		agent_typing: 'ភ្នាក់ងារកំពុងវាយ',
		email_placeholder: 'អ៊ីមែលរបស់អ្នក@ឧទាហរណ៍.com',
	},
	vi: {
		menu_title: 'Chúng tôi có thể giúp gì cho bạn?',
		feedback: 'Phản hồi',
		feedback_desc: 'Chia sẻ suy nghĩ của bạn',
		bug_report: 'Báo cáo lỗi',
		bug_desc: 'Có gì đó không hoạt động?',
		feature_request: 'Yêu cầu tính năng',
		feature_desc: 'Đề xuất cải tiến',
		reviews: 'Để lại đánh giá',
		reviews_desc: 'Chia sẻ trải nghiệm của bạn',
		live_chat: 'Trò chuyện trực tiếp',
		live_chat_desc: 'Nói chuyện với chúng tôi',
		submit: 'Gửi',
		back: 'Quay lại',
		next: 'Tiếp theo',
		skip: 'Bỏ qua',
		close: 'Đóng',
		thank_you: 'Cảm ơn bạn!',
		feedback_received: 'Chúng tôi đánh giá cao phản hồi của bạn.',
		bug_title: 'Báo cáo lỗi',
		bug_description: 'Mô tả vấn đề',
		bug_placeholder: 'Vui lòng mô tả điều gì đã xảy ra sai...',
		screenshot_optional: 'Ảnh chụp màn hình (tùy chọn)',
		attach_screenshot: 'Đính kèm ảnh chụp',
		your_name: 'Tên của bạn',
		your_email: 'Email của bạn',
		optional: 'Tùy chọn',
		your_name_optional: 'Tên của bạn (Tùy chọn)',
		your_email_optional: 'Email của bạn (Tùy chọn)',
		feature_title: 'Yêu cầu tính năng',
		search_features: 'Tìm kiếm tính năng...',
		suggest_feature: 'Đề xuất tính năng mới',
		suggest_a_feature: 'Đề xuất tính năng',
		upvote: 'Ủng hộ',
		upvoted: 'Đã ủng hộ',
		share_experience: 'Chia sẻ trải nghiệm của bạn (tối thiểu 10 ký tự)',
		nps_question: 'Khả năng bạn giới thiệu chúng tôi là bao nhiêu?',
		csat_question: 'Bạn hài lòng chứ?',
		overall_rating: 'Đánh giá tổng thể',
		overall_rating_optional: 'Đánh giá tổng thể (tùy chọn)',
		no_feature_requests: 'Chưa có yêu cầu tính năng nào',
		be_first_suggest: 'Hãy là người đầu tiên đề xuất tính năng!',
		title: 'Tiêu đề',
		description: 'Mô tả',
		feature_idea_brief: 'Mô tả ngắn gọn về ý tưởng của bạn',
		feature_idea_detail: 'Mô tả chi tiết ý tưởng của bạn...',
		describe_issue: 'Mô tả vấn đề (tối thiểu 10 ký tự)',
		what_trying_to_do: 'Bạn đang cố gắng làm gì?...',
		name: 'Tên',
		email: 'Email',
		required_field: 'bắt buộc',
		social_media_link: 'Link mạng xã hội (tùy chọn)',
		i_agree_feedback: 'Tôi đồng ý rằng phản hồi của tôi có thể được sử dụng công khai cho mục đích tiếp thị hoặc làm chứng thực.',
		continue: 'Tiếp tục',
		providing_email_help: 'Cung cấp email giúp chúng tôi liên lạc nếu có thắc mắc.',
		attach_screenshot_optional: 'Đính kèm ảnh chụp (Tùy chọn, Tối đa 5MB)',
		share_your_feedback: 'Chia sẻ phản hồi của bạn',
		email_required_verification: 'Email (bắt buộc để xác minh)',
		name_optional: 'Tên (tùy chọn)',
		full_name_or_company: 'Tên đầy đủ hoặc tên công ty',
		tell_us_what_you_think: 'Cho chúng tôi biết suy nghĩ của bạn...',
		search_feature_requests: 'Tìm kiếm yêu cầu tính năng...',
		start_conversation: 'Bắt đầu cuộc trò chuyện',
		work_email: 'Email công việc',
		email_follow_up: 'Chúng tôi sẽ sử dụng email của bạn để liên hệ nếu bạn rời khỏi trang.',
		hi_greeting: 'Xin chào',
		chat_name: 'Tên',
		submit_feedback: 'Gửi phản hồi',
		start_chat: 'Bắt đầu chat',
		not_likely: 'Không chắc',
		very_likely: 'Rất có khả năng',
		your_answer: 'Câu trả lời của bạn...',
		attach_screenshot_btn: 'Đính kèm ảnh chụp',
		offline: 'Ngoại tuyến',
		leave_us_message: 'Để lại tin nhắn',
		team_offline: 'Nhóm của chúng tôi hiện đang ngoại tuyến. Chúng tôi sẽ phản hồi bạn sớm nhất có thể.',
		say_hi: 'Chào, chúng tôi sẽ trả lời ở đây.',
		agent_typing: 'Nhân viên đang nhập',
		email_placeholder: 'email.cua.ban@vidu.com',
	},
}

export const WidgetPreview = ({
	config,
	showManualTrigger = false,
}: WidgetPreviewProps) => {
	// Get current language from i18n
	const { i18n } = useTranslation()
	
	// Get translation function
	const t = (key: string): string => {
		// Use config.language if provided, otherwise use i18n language
		// Extract base language code (e.g., 'fr' from 'fr-FR')
		const i18nLang = i18n.language?.split('-')[0] || 'en'
		const lang = config.language || i18nLang
		return translations[lang]?.[key] || translations.en[key] || key
	}
	// --- Core State ---
	const [isOpen, setIsOpen] = useState(false)
	const [viewMode, setViewMode] = useState<ViewMode>('mainMenu')

	useEffect(() => {
		const openFromThemePreview = () => {
			setViewMode('mainMenu')
			setIsOpen(true)
		}
		const closeFromThemePreview = () => {
			setIsOpen(false)
		}
		if (typeof window !== 'undefined') {
			window.addEventListener('feedbask:open-widget-preview', openFromThemePreview)
			window.addEventListener(
				'feedbask:close-widget-preview',
				closeFromThemePreview
			)
		}
		return () => {
			if (typeof window !== 'undefined') {
				window.removeEventListener(
					'feedbask:open-widget-preview',
					openFromThemePreview
				)
				window.removeEventListener(
					'feedbask:close-widget-preview',
					closeFromThemePreview
				)
			}
		}
	}, [])

	// --- Primary Feedback State (Survey/Review) ---
	const [selectedRating, setSelectedRating] = useState<number | null>(null)
	const [surveyAnswers, setSurveyAnswers] = useState<Record<string, string>>({})
	const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
	const [testimonialText, setTestimonialText] = useState('')
	const [testimonialConsent, setTestimonialConsent] = useState(false)

	// --- Bug Reporting State ---
	const [bugDescription, setBugDescription] = useState('')
	const [screenshotFile, setScreenshotFile] = useState<File | null>(null)
	const [screenshotPreview, setScreenshotPreview] = useState<string | null>(
		null
	)
	const [bugReporterName, setBugReporterName] = useState('') // New state
	const [bugReporterEmail, setBugReporterEmail] = useState('') // New state
	const [isSubmittingBug, setIsSubmittingBug] = useState(false)
	const [bugSubmitError, setBugSubmitError] = useState<string | null>(null)


	const [contactMessage, setContactMessage] = useState('')
	const [contactSenderName, setContactSenderName] = useState('')
	const [contactSenderEmail, setContactSenderEmail] = useState('')
	const [contactSubmitError, setContactSubmitError] = useState<string | null>(null)
	const [isSubmittingContact, setIsSubmittingContact] = useState(false)
	const fileInputRef = useRef<HTMLInputElement>(null)

	// --- Feature Request State ---
	const [featureSearchQuery, setFeatureSearchQuery] = useState('')
	const [featureRequests, setFeatureRequests] = useState<FeatureRequest[]>([])
	const [isLoadingFeatures, setIsLoadingFeatures] = useState(false)
	const [featureLoadError, setFeatureLoadError] = useState<string | null>(null)
	const [newFeatureTitle, setNewFeatureTitle] = useState('')
	const [newFeatureDescription, setNewFeatureDescription] = useState('')
	const [newFeatureCategory, setNewFeatureCategory] = useState('')
	const [submitterName, setSubmitterName] = useState('') // New state
	const [submitterEmail, setSubmitterEmail] = useState('') // New state
	const [isSubmittingFeature, setIsSubmittingFeature] = useState(false)
	const [featureSubmitError, setFeatureSubmitError] = useState<string | null>(
		null
	)
	const [upvotedFeatures, setUpvotedFeatures] = useState<
		Record<string, boolean>
	>({})
	const [upvotingFeature, setUpvotingFeature] = useState<string | null>(null)

	// --- Dedicated Review State ---
	const [reviewRating, setReviewRating] = useState<number | null>(null)
	const [hoverRating, setHoverRating] = useState<number | null>(null)
	const [reviewText, setReviewText] = useState('')
	const [reviewConsent, setReviewConsent] = useState(false)
	const [isSubmittingReview, setIsSubmittingReview] = useState(false)
	const [reviewSubmitError, setReviewSubmitError] = useState<string | null>(
		null
	)

	// Add new state for the verification flow
	const [reviewEmail, setReviewEmail] = useState('')
	const [reviewName, setReviewName] = useState('')
	const [reviewSocialLink, setReviewSocialLink] = useState('')
	const [verificationStep, setVerificationStep] = useState<
		'form' | 'verification'
	>('form')
	const [verificationCode, setVerificationCode] = useState('')
	const [isRequestingCode, setIsRequestingCode] = useState(false)
	const [isVerifyingCode, setIsVerifyingCode] = useState(false)
	const [verificationError, setVerificationError] = useState<string | null>(
		null
	)
	const [canResendCode, setCanResendCode] = useState(false)

	// --- General State ---
	const [lastSubmissionType, setLastSubmissionType] = useState<
		'feedback' | 'bug' | 'contact' | 'feature' | 'review' | null
	>(null)

	// Add this to the other state declarations at the top level
	const [showAllFeatures, setShowAllFeatures] = useState(false)

	// --- Live Chat State ---
	const [chatName, setChatName] = useState('')
	const [chatEmail, setChatEmail] = useState('')
	const [chatConversationId, setChatConversationId] = useState<string | null>(
		null
	)
	const [chatMessages, setChatMessages] = useState<
		Array<{
			id: string
			sender_type: 'visitor' | 'agent'
			content: string | null
			media_url: string | null
			created_at: string
			agent_id?: string | null
		}>
	>([])
	const [chatAgentProfile, setChatAgentProfile] = useState<{
		user_id: string
		full_name: string
		job_title?: string
		avatar_url?: string
		bio?: string
		status: 'online' | 'away' | 'offline'
		status_message?: string
		expertise_areas?: string[]
		languages?: string[]
		response_time_minutes?: number
	} | null>(null)
	const [chatMessage, setChatMessage] = useState('')
	const [isLoadingChat, setIsLoadingChat] = useState(false)
	const [chatError, setChatError] = useState<string | null>(null)
	const [isSubmittingChatMessage, setIsSubmittingChatMessage] = useState(false)
	const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null)
	const [isImageModalOpen, setIsImageModalOpen] = useState(false)
	const [availableAgents, setAvailableAgents] = useState<any[]>([])
	const [isLoadingAgents, setIsLoadingAgents] = useState(true)
	const chatMessagesEndRef = useRef<HTMLDivElement>(null)
	const chatScrollContainerRef = useRef<HTMLDivElement>(null)
	const [isUserScrolledUp, setIsUserScrolledUp] = useState(false)
	const [hasNewMessages, setHasNewMessages] = useState(false)
	const [agentTyping, setAgentTyping] = useState(false)
	const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
	const presenceChannelRef = useRef<any>(null)

	// Chat history state
	const [chatHistoryLimited, setChatHistoryLimited] = useState(false)
	const [chatHistoryCutoff, setChatHistoryCutoff] = useState<string | null>(null)

	// --- Sidebar State (moved to top level to fix hooks order warning) ---
	let sidebarState: 'expanded' | 'collapsed' = 'collapsed'
	let isMobile = false

	try {
		const sidebar = useSidebar()
		sidebarState = sidebar.state
		isMobile = sidebar.isMobile
	} catch (error) {
		// useSidebar is not available (e.g., in test mode), use defaults
		sidebarState = 'collapsed'
		isMobile = false
	}

	// Helper function to get chat session storage key
	const getChatSessionKey = () => `feedbask_chat_session_${config.id}`
	const getChatMessagesKey = () => `feedbask_chat_messages_${config.id}`
	const getChatMetaKey = () => `feedbask_chat_meta_${config.id}`

	// Handle image download
	const handleDownloadImage = async (url: string) => {
		try {
			const response = await fetch(url)
			const blob = await response.blob()
			const downloadUrl = window.URL.createObjectURL(blob)
			const link = document.createElement('a')
			link.href = downloadUrl
			link.download = `chat-image-${Date.now()}.png`
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			window.URL.revokeObjectURL(downloadUrl)
		} catch (error) {
			// Error handling
		}
	}

	// Smart scroll behavior
	useEffect(() => {
		if (chatMessagesEndRef.current && viewMode === 'chatView' && !isUserScrolledUp) {
			// Only auto-scroll if user hasn't scrolled up
			setTimeout(() => {
				chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
			}, 100)
		}
	}, [chatMessages, viewMode, isUserScrolledUp])

	// Detect when user scrolls
	const handleChatScroll = () => {
		if (!chatScrollContainerRef.current) return
		const { scrollTop, scrollHeight, clientHeight } = chatScrollContainerRef.current
		const isAtBottom = scrollHeight - scrollTop - clientHeight < 50
		setIsUserScrolledUp(!isAtBottom)
		if (isAtBottom) {
			setHasNewMessages(false)
		}
	}

	// Mark new messages when scrolled up
	useEffect(() => {
		if (isUserScrolledUp && chatMessages.length > 0) {
			const lastMessage = chatMessages[chatMessages.length - 1]
			if (lastMessage.sender_type === 'agent') {
				setHasNewMessages(true)
			}
		}
	}, [chatMessages, isUserScrolledUp])

	// Default feedback button settings
	const feedbackButton = {
		show: true, // Always show the feedback button
		text: config.feedbackButton?.text || 'Feedback',
		color: config.feedbackButton?.color || config.primaryColor || '#0066FF',
		textColor: config.feedbackButton?.textColor || '#FFFFFF',
	}

	// Track animation direction
	const [direction, setDirection] = useState(0)
	

	const navigateTo = async (newView: ViewMode) => {
		// If navigating to chat view and we have an existing conversation, fetch latest messages
		if (newView === 'chatView' && chatConversationId) {
			try {
				const backendUrl = getApiBaseUrl()
				const endUserIdentifier = generateEndUserIdentifier()

				const response = await fetch(
					`${backendUrl}/api/public/chat/${chatConversationId}/messages?identifier=${endUserIdentifier}`,
					{
						method: 'GET',
						headers: {
							'Content-Type': 'application/json',
						},
					}
				)

				if (response.ok) {
					const data = await response.json()
					if (data.messages && data.messages.length > 0) {
						setChatMessages(data.messages)
						// Update localStorage with latest messages
						const messagesKey = getChatMessagesKey()
						localStorage.setItem(messagesKey, JSON.stringify(data.messages))
					}
					if (typeof data.history_limited !== 'undefined') {
						setChatHistoryLimited(!!data.history_limited)
						setChatHistoryCutoff(data.history_cutoff || null)
						const metaKey = getChatMetaKey()
						localStorage.setItem(metaKey, JSON.stringify({ history_limited: !!data.history_limited, history_cutoff: data.history_cutoff || null }))
					}
					// Set agent profile if available
					if (data.agent_profile) {
						setChatAgentProfile(data.agent_profile)
					}
				}
			} catch (error) {
				// Error handling
			}
		}

		setDirection(viewMode === 'mainMenu' ? 1 : -1)
		setViewMode(newView)
	}

	// Helper functions for layout configuration
	const getMenuPadding = (): string => {
		if (config.layout?.menuPadding) return config.layout.menuPadding
		if (config.menuItemStyle?.layout === 'separate') return 'px-4'
		return 'py-2'
	}

	const getItemSpacing = (): string => {
		if (config.layout?.itemSpacing) return config.layout.itemSpacing
		if (config.menuItemStyle?.layout === 'separate') return 'space-y-3'
		return ''
	}

	const isGlassmorphismEnabled = (): boolean => {
		return config.glassmorphism?.enabled === true
	}

	const getInputBackgroundColor = (): string => {
		const bgColor = config.backgroundColor || '#FFFFFF'

		// Determine if this is a dark theme
		const isDarkTheme =
			config.textColor === '#FFFFFF' ||
			config.textColor === '#F4F4F5' ||
			config.textColor === '#F3F4F6'

		// If glassmorphism is enabled, adjust based on theme
		if (isGlassmorphismEnabled()) {
			if (isDarkTheme) {
				// Dark theme with glassmorphism: use dark semi-transparent background
				return 'rgba(0, 0, 0, 0.3)'
			} else {
				// Light theme with glassmorphism: use light semi-transparent background
				return 'rgba(255, 255, 255, 0.95)'
			}
		}

		// If it's already an rgba value, use it as is
		if (bgColor.startsWith('rgba')) {
			return bgColor
		}

		// If it's a hex color, determine if we need to adjust for contrast
		if (bgColor.startsWith('#')) {
			if (isDarkTheme) {
				// Dark theme: make input background lighter for contrast with white text
				return 'rgba(255, 255, 255, 0.1)' // Light semi-transparent overlay
			} else {
				// Light theme: use the background color directly
				return bgColor
			}
		}

		return bgColor
	}

	const getBorderColor = (defaultColor: string = '#E5E7EB'): string => {
		if (isGlassmorphismEnabled()) {
			return config.glassmorphism?.borderColor || config.textColor + '20'
		}

		// Use theme-specific border colors
		if (config.menuItemStyle?.borderColor) {
			return config.menuItemStyle.borderColor
		}

		if (config.brandingStyle?.borderColor) {
			return config.brandingStyle.borderColor
		}

		return defaultColor
	}

	const getTransparentBackgroundColor = (): string => {
		return isGlassmorphismEnabled()
			? 'transparent'
			: config.backgroundColor || '#FFFFFF'
	}

	// Consolidated styling helper for forms
	const getFormFieldStyle = () => ({
		color: '#333',
		backgroundColor: getInputBackgroundColor(),
		borderColor: config.primaryColor,
	})

	const isBottomModuleNavigationEnabled = (): boolean => {
		return config.moduleNavigation?.enabled === true
	}

	const getBottomModules = (): Array<{
		id: BottomModuleId
		label: string
		icon: React.ElementType
		targetView: ViewMode
	}> => {
		const preferredModules =
			config.moduleNavigation?.modules ||
			([
				'feedback',
				'review',
				'mailbox',
				'bug-report',
				'feature-request',
				'roadmap',
				'live-chat',
			] as BottomModuleId[])

		const modulesById: Record<
			BottomModuleId,
			{
				id: BottomModuleId
				label: string
				icon: React.ElementType
				targetView: ViewMode
				enabled: boolean
			}
		> = {
			feedback: {
				id: 'feedback',
				label: t('feedback'),
				icon: Star,
				targetView: 'surveyStep',
				enabled: !!config.enable_survey,
			},
			review: {
				id: 'review',
				label: t('reviews'),
				icon: MessageSquare,
				targetView: 'reviewForm',
				enabled: !!config.enable_reviews,
			},
			mailbox: {
				id: 'mailbox',
				label: 'Mailbox',
				icon: Mail,
				targetView: 'contactForm',
				enabled: !!config.enable_contact_messages,
			},
			'bug-report': {
				id: 'bug-report',
				label: t('bug_report'),
				icon: Bug,
				targetView: 'bugForm',
				enabled: !!config.enable_bug_reporting,
			},
			'feature-request': {
				id: 'feature-request',
				label: t('feature_request'),
				icon: LightbulbIcon,
				targetView: 'featureForm',
				enabled: !!config.enable_feature_requests,
			},
			roadmap: {
				id: 'roadmap',
				label: 'Roadmap',
				icon: Search,
				targetView: 'featureList',
				enabled: !!config.enable_feature_requests,
			},
			'live-chat': {
				id: 'live-chat',
				label: t('live_chat'),
				icon: MessageCircle,
				targetView: chatConversationId ? 'chatView' : 'chatInit',
				enabled: !!config.enable_live_chat,
			},
		}

		return preferredModules
			.map((moduleId) => modulesById[moduleId])
			.filter((moduleItem) => moduleItem?.enabled)
			.map(({ enabled: _enabled, ...moduleItem }) => moduleItem)
	}

	const shouldShowBottomModuleNav = (): boolean => {
		if (!isBottomModuleNavigationEnabled()) return false
		const bottomModules = getBottomModules()
		if (bottomModules.length < 2) return false
		return (
			viewMode === 'surveyStep' ||
			viewMode === 'reviewForm' ||
			viewMode === 'contactForm' ||
			viewMode === 'bugForm' ||
			viewMode === 'featureForm' ||
			viewMode === 'featureList' ||
			viewMode === 'chatInit' ||
			viewMode === 'chatView'
		)
	}

	// Reset ALL state when widget is closed or flow is completed
	const resetWidget = (preserveChat: boolean = true) => {
		setIsOpen(false)
		setViewMode('mainMenu')
		setSelectedRating(null)
		setSurveyAnswers({})
		setCurrentQuestionIndex(0)
		setTestimonialText('')
		setTestimonialConsent(false)
		setBugDescription('')
		setContactMessage('')
		setScreenshotFile(null)
		setScreenshotPreview(null)
		setIsSubmittingBug(false)
		setIsSubmittingContact(false)
		setBugSubmitError(null)
		setContactSubmitError(null)
		if (fileInputRef.current) {
			fileInputRef.current.value = ''
		}
		setFeatureSearchQuery('')
		setNewFeatureTitle('')
		setNewFeatureDescription('')
		setNewFeatureCategory('')
		setIsSubmittingFeature(false)
		setFeatureSubmitError(null)

		// Reset review state
		setReviewRating(null)
		setHoverRating(null)
		setReviewText('')
		setReviewConsent(false)
		setIsSubmittingReview(false)
		setReviewSubmitError(null)

		// Reset verification state
		setReviewEmail('')
		setReviewName('')
		setReviewSocialLink('')
		setVerificationStep('form')
		setVerificationCode('')
		setIsRequestingCode(false)
		setIsVerifyingCode(false)
		setVerificationError(null)

		setLastSubmissionType(null)
		setBugReporterName('')
		setContactSenderName('')
		setBugReporterEmail('')
		setContactSenderEmail('')
		setSubmitterName('')
		setSubmitterEmail('')

		// Only reset live chat state if not preserving it
		if (!preserveChat) {
			setChatName('')
			setChatEmail('')
			setChatConversationId(null)
			setChatAgentProfile(null)
			setChatMessages([])
			setChatMessage('')
			setIsLoadingChat(false)
			setChatError(null)
			setIsSubmittingChatMessage(false)

			// Clear localStorage for chat
			const sessionKey = getChatSessionKey()
			const messagesKey = getChatMessagesKey()
			localStorage.removeItem(sessionKey)
			localStorage.removeItem(messagesKey)
		} else {
			// Just clear current message and errors when preserving chat
			setChatMessage('')
			setChatError(null)
		}
	}

	// Reset view to main menu when widget is opened, but check for existing chat session
	useEffect(() => {
		if (isOpen) {
			// Check if there's an existing chat session
			const sessionKey = getChatSessionKey()
			const messagesKey = getChatMessagesKey()
			const storedSession = localStorage.getItem(sessionKey)

			if (storedSession && config.enable_live_chat) {
				try {
					const session = JSON.parse(storedSession)
					if (session.conversationId && session.name && session.email) {
						// Restore chat session
						setChatConversationId(session.conversationId)
						setChatName(session.name)
						setChatEmail(session.email)

						// Fetch latest messages from server
						const fetchLatestMessages = async () => {
							try {
								const backendUrl = getApiBaseUrl()
								const endUserIdentifier = generateEndUserIdentifier()

								const response = await fetch(
									`${backendUrl}/api/public/chat/${session.conversationId}/messages?identifier=${endUserIdentifier}`,
									{
										method: 'GET',
										headers: {
											'Content-Type': 'application/json',
										},
									}
								)

								if (response.ok) {
									const data = await response.json()
									if (data.messages && data.messages.length > 0) {
										setChatMessages(data.messages)
										// Update localStorage with latest messages
										localStorage.setItem(
											messagesKey,
											JSON.stringify(data.messages)
										)
									}
									if (typeof data.history_limited !== 'undefined') {
										setChatHistoryLimited(!!data.history_limited)
										setChatHistoryCutoff(data.history_cutoff || null)
										const metaKey = getChatMetaKey()
										localStorage.setItem(metaKey, JSON.stringify({ history_limited: !!data.history_limited, history_cutoff: data.history_cutoff || null }))
									}
									// Set agent profile if available
									if (data.agent_profile) {
										setChatAgentProfile(data.agent_profile)
									}
								} else {
									// Fall back to stored messages if server fetch fails
									const storedMessages = localStorage.getItem(messagesKey)
									if (storedMessages) {
										try {
											let messages = JSON.parse(storedMessages)
											const metaRaw = localStorage.getItem(getChatMetaKey())
											if (metaRaw) {
												const meta = JSON.parse(metaRaw)
												if (meta.history_limited && meta.history_cutoff) {
													const cutoff = new Date(meta.history_cutoff).getTime()
													messages = messages.filter((m: any) => new Date(m.created_at).getTime() >= cutoff)
												}
												setChatHistoryLimited(!!meta.history_limited)
												setChatHistoryCutoff(meta.history_cutoff || null)
											}
											setChatMessages(messages)
										} catch (e) {
											// Error handling
										}
									}
								}
							} catch (error) {
								// Fall back to stored messages on error
								const storedMessages = localStorage.getItem(messagesKey)
								if (storedMessages) {
									try {
										let messages = JSON.parse(storedMessages)
										const metaRaw = localStorage.getItem(getChatMetaKey())
										if (metaRaw) {
											const meta = JSON.parse(metaRaw)
											if (meta.history_limited && meta.history_cutoff) {
												const cutoff = new Date(meta.history_cutoff).getTime()
												messages = messages.filter((m: any) => new Date(m.created_at).getTime() >= cutoff)
											}
											setChatHistoryLimited(!!meta.history_limited)
											setChatHistoryCutoff(meta.history_cutoff || null)
										}
										setChatMessages(messages)
									} catch (e) {
										// Error handling
									}
								}
							}
						}

						fetchLatestMessages()

						// Don't automatically navigate to chat view, let user choose
						setViewMode('mainMenu')
					} else {
						setViewMode('mainMenu')
					}
				} catch (e) {
					setViewMode('mainMenu')
				}
			} else {
				setViewMode('mainMenu')
			}
		}
	}, [isOpen, config.enable_live_chat, config.id])

	useEffect(() => {
		if (!isOpen) return
		if (!isBottomModuleNavigationEnabled()) {
			setViewMode('mainMenu')
			return
		}
		if (config.moduleNavigation?.defaultToFirstModule === false) return

		const bottomModules = getBottomModules()
		if (bottomModules.length > 0) {
			setViewMode(bottomModules[0].targetView)
		}
	}, [isOpen, config.moduleNavigation, config.enable_live_chat, chatConversationId])

	// Subscribe to real-time chat messages and presence
	useEffect(() => {
		if (!chatConversationId) {
			return
		}

		const channel = supabase
			.channel(`widget_chat:${chatConversationId}`)

		// Track agent typing presence
		const presenceChannel = supabase
			.channel(`presence:${chatConversationId}`, {
				config: {
					presence: {
						key: chatConversationId,
					},
				},
			})
			.on('presence', { event: 'sync' }, () => {
				const state = presenceChannel.presenceState()
				const agentPresence = Object.values(state).find(
					(presence: any) => presence[0]?.user_type === 'agent' && presence[0]?.typing
				)
					setAgentTyping(!!agentPresence)
			})
			.subscribe((status, error) => {
				})
			
		// Store presence channel in ref for use in input handlers
		presenceChannelRef.current = presenceChannel

		// Set up the main chat channel with broadcast messaging
		channel
			.on(
				'broadcast',
				{ event: 'new_message' },
				(payload) => {
						const newMessage = payload.payload?.message as {
						id: string
						sender_type: 'visitor' | 'agent'
						content: string | null
						media_url: string | null
						created_at: string
						agent_id?: string | null
						conversation_id?: string
					}

					if (!newMessage) {
							return
					}


					// Only process messages for this conversation
					if (
						newMessage.conversation_id &&
						newMessage.conversation_id !== chatConversationId
					) {
						return
					}


					// Only add messages that aren't already in the list (avoid duplicates)
					setChatMessages((prev) => {
						// Filter out any temporary messages
						const filtered = prev.filter((msg) => !msg.id.startsWith('temp-'))
						const exists = filtered.some((msg) => msg.id === newMessage.id)

						if (!exists) {
							const updatedMessages = [...filtered, newMessage]
							// Store updated messages in localStorage
							const messagesKey = getChatMessagesKey()
							localStorage.setItem(messagesKey, JSON.stringify(updatedMessages))
							return updatedMessages
						}
						return filtered
					})
				}
			)
			.on(
				'postgres_changes',
				{
					event: 'UPDATE',
					schema: 'public',
					table: 'chat_conversations',
					filter: `id=eq.${chatConversationId}`,
				},
				(payload) => {
					const updatedConversation = payload.new as any

					// If conversation was closed from dashboard
					if (updatedConversation.status === 'closed') {
						// Show a message that the conversation was closed
						setChatMessages((prev) => [
							...prev,
							{
								id: `system-${Date.now()}`,
								sender_type: 'agent' as const,
								content:
									'This conversation has been closed by the support team.',
								media_url: null,
								created_at: new Date().toISOString(),
								agent_id: null,
							},
						])

						// Clear local storage after a delay
						setTimeout(() => {
							const sessionKey = getChatSessionKey()
							const messagesKey = getChatMessagesKey()
							localStorage.removeItem(sessionKey)
							localStorage.removeItem(messagesKey)

							// Reset state
							setChatName('')
							setChatEmail('')
							setChatConversationId(null)
							setChatAgentProfile(null)
							setChatMessages([])
							setChatMessage('')
							setChatError(null)

							// Go back to chat init
							setViewMode('chatInit')
						}, 3000)
					}
				}
			)
			.subscribe((status, error) => {
					if (status === 'SUBSCRIBED') {
							// Fetch latest messages after subscribing to ensure we didn't miss any
					const fetchLatest = async () => {
						try {
							const backendUrl = getApiBaseUrl()
							const endUserIdentifier = generateEndUserIdentifier()

							const response = await fetch(
								`${backendUrl}/api/public/chat/${chatConversationId}/messages?identifier=${endUserIdentifier}`,
								{
									method: 'GET',
									headers: {
										'Content-Type': 'application/json',
									},
								}
							)

							if (response.ok) {
								const data = await response.json()
								if (data.messages && data.messages.length > 0) {
									setChatMessages(data.messages)
									const messagesKey = getChatMessagesKey()
									localStorage.setItem(
										messagesKey,
										JSON.stringify(data.messages)
									)
								}
								if (typeof data.history_limited !== 'undefined') {
									setChatHistoryLimited(!!data.history_limited)
									setChatHistoryCutoff(data.history_cutoff || null)
									const metaKey = getChatMetaKey()
									localStorage.setItem(metaKey, JSON.stringify({ history_limited: !!data.history_limited, history_cutoff: data.history_cutoff || null }))
								}
								// Set agent profile if available
								if (data.agent_profile) {
									setChatAgentProfile(data.agent_profile)
								}
							}
						} catch (error) {
							// Error handling
						}
					}

					// Delay slightly to ensure subscription is fully established
					setTimeout(fetchLatest, 500)
				}
			})

		return () => {
			supabase.removeChannel(channel)
			supabase.removeChannel(presenceChannel)
			presenceChannelRef.current = null
		}
	}, [chatConversationId])

	// Debug logging for conversation ID changes
	useEffect(() => {
	}, [chatConversationId, chatMessages.length, viewMode])

	// Load feature requests when feature list is opened
	useEffect(() => {
		if (viewMode === 'featureList' && config.id) {
			const loadFeatures = async () => {
				setIsLoadingFeatures(true)
				setFeatureLoadError(null)
				try {
					const features = await fetchFeatureRequests(
						config.id!,
						config.program_id
					)

					setFeatureRequests(features)
				} catch (error) {
					setFeatureLoadError('Failed to load feature requests')
				} finally {
					setIsLoadingFeatures(false)
				}
			}
			loadFeatures()
		}
	}, [viewMode, config.id, config.program_id])

	// Fetch available agents when chat init view is opened
	useEffect(() => {
		if (viewMode === 'chatInit' && config.program_id) {
			const fetchAgents = async () => {
				setIsLoadingAgents(true)
				try {
					const backendUrl = getApiBaseUrl()
					const response = await fetch(
						`${backendUrl}/api/public/agent-availability/${config.program_id}`
					)
					if (response.ok) {
						const data = await response.json()
						if (data.agents && data.agents.length > 0) {
							setAvailableAgents(data.agents)
						}
					}
				} catch (error) {
					console.error('Failed to fetch agents:', error)
				} finally {
					setIsLoadingAgents(false)
				}
			}
			fetchAgents()
		}
	}, [viewMode, config.program_id])

	// Check upvote status for all features when they're loaded
	useEffect(() => {
		if (viewMode === 'featureList' && featureRequests.length > 0) {
			const checkFeatureUpvotes = async () => {
				const statuses: Record<string, boolean> = {}

				// Process features in parallel
				await Promise.all(
					featureRequests.map(async (feature, index) => {
						if (!feature || !feature.id) {
							return
						}

						try {
							const isUpvoted = await checkUpvoteStatus(feature.id)
							statuses[feature.id] = isUpvoted
						} catch (error) {
							statuses[feature.id] = false
						}
					})
				)

				setUpvotedFeatures(statuses)
			}

			checkFeatureUpvotes()
		}
	}, [viewMode, featureRequests])

	// Handle feature upvote
	const handleUpvote = async (featureId: string) => {
		if (!featureId || featureId === 'undefined') {
			return
		}

		if (upvotingFeature) {
			return // Prevent multiple concurrent upvotes
		}

		setUpvotingFeature(featureId)
		try {
			const result = await upvoteFeature(featureId)

			// Update the local state with the upvoted status
			setUpvotedFeatures((prev) => ({
				...prev,
				[featureId]: true,
			}))

			// If we got an updated feature with new upvote count, merge it properly
			if (result.feature && result.feature.upvote_count) {
				setFeatureRequests((prev) =>
					prev.map((f) =>
						f.id === featureId
							? { ...f, upvote_count: result.feature!.upvote_count }
							: f
					)
				)
			} else if (result.upvoted) {
				// Already upvoted but no feature data returned, mark as upvoted
				setUpvotedFeatures((prev) => ({
					...prev,
					[featureId]: true,
				}))
			}
		} catch (error) {
			// Error handling
		} finally {
			setUpvotingFeature(null)
		}
	}

	// Get button position styles
	const getButtonPosition = (): React.CSSProperties => {
		const position = config.position || 'bottom-right'
		const base: React.CSSProperties = {
			position: 'fixed',
			zIndex: 2147483000,
		}

		// Use sidebar state from component level (already defined at top)

		// Calculate sidebar width offset for left-positioned widgets
		const getSidebarOffset = () => {
			if (isMobile) return 0 // No sidebar on mobile
			if (sidebarState === 'expanded') return 224 // 14rem = 224px
			if (sidebarState === 'collapsed') return 48 // 3rem = 48px
			return 0
		}

		switch (position) {
			case 'middle-left':
				return {
					...base,
					left: `${getSidebarOffset()}px`,
					top: '50%',
					transform: 'translateY(-50%)',
				}
			case 'middle-right':
				return {
					...base,
					right: '0px',
					top: '50%',
					transform: 'translateY(-50%)',
				}
			case 'bottom-left':
				return {
					...base,
					left: `${getSidebarOffset() + 20}px`,
					bottom: '20px',
				}
			case 'bottom-center':
				return {
					...base,
					left: '50%',
					bottom: '0px',
					transform: 'translateX(-50%)',
				}
			default: // bottom-right
				return {
					...base,
					right: '20px',
					bottom: '20px',
				}
		}
	}

	// Get modal position styles - center modal for wall-mounted positions
	const getModalPosition = (): React.CSSProperties => {
		const position = config.position || 'bottom-right'
		const base: React.CSSProperties = {
			position: 'fixed',
			zIndex: 2147483001,
		}

		// Use sidebar state from component level (already defined at top)

		// Calculate sidebar width offset for left-positioned widgets
		const getSidebarOffset = () => {
			if (isMobile) return 0 // No sidebar on mobile
			if (sidebarState === 'expanded') return 224 // 14rem = 224px
			if (sidebarState === 'collapsed') return 48 // 3rem = 48px
			return 0
		}

		// For middle-left and middle-right positions, center the modal (transform handled in transformTemplate)
		if (position === 'middle-left' || position === 'middle-right') {
			return {
				...base,
				left: '50%',
				top: '50%',
				right: 'auto',
				bottom: 'auto',
			}
		}

		// For other positions, use the same logic as button positioning
		switch (position) {
			case 'bottom-left':
				return {
					...base,
					left: `${getSidebarOffset() + 20}px`,
					bottom: '20px',
				}
			case 'bottom-center':
				return {
					...base,
					left: '50%',
					bottom: '20px',
					transform: 'translateX(-50%)',
				}
			default: // bottom-right
				return {
					...base,
					right: '20px',
					bottom: '20px',
				}
		}
	}

	// --- Submission Handlers (will call APIs and set viewMode to 'thankYou') ---
	const handlePrimaryFeedbackSubmit = async (): Promise<void> => {
		setIsSubmittingFeature(true)

		try {
			// Get the API URL
			const backendUrl = getApiBaseUrl()

			// Gather user metadata
			const userMetadata = {
				pageUrl: window.location.href,
				pageTitle: document.title,
				userAgent: navigator.userAgent,
				screenWidth: window.screen.width,
				screenHeight: window.screen.height,
			}

			// Construct the payload based on feedback type
			let score = null
			let responseData = {}

			if (
				config.primary_feedback_type === 'nps' ||
				config.primary_feedback_type === 'csat'
			) {
				score = selectedRating
				// Store the main question and answer for NPS/CSAT
				responseData = {
					customAnswers: {
						[config.id || 'main']: {
							questionText:
								config.question ||
								(config.primary_feedback_type === 'nps'
									? 'How likely are you to recommend us?'
									: 'How satisfied were you?'),
							answer: String(selectedRating),
							type: 'scale',
						},
					},
				}
			} else if (config.primary_feedback_type === 'custom') {
				// Enhanced custom survey response structure
				const customAnswers: Record<string, any> = {}

				// Process each answer with its corresponding question text
				Object.entries(surveyAnswers).forEach(([questionId, answer]) => {
					const question = config.customQuestions?.find(
						(q) => q.id === questionId
					)
					if (question) {
						customAnswers[questionId] = {
							questionText: question.question,
							answer: answer,
							type: question.type,
						}
					}
				})

				responseData = { customAnswers }

				// If there's a numeric scale question, use its value as the main score
				const scaleQuestion = config.customQuestions?.find(
					(q) => q.type === 'scale'
				)
				if (scaleQuestion && surveyAnswers[scaleQuestion.id]) {
					score = parseInt(surveyAnswers[scaleQuestion.id], 10)
				}
			} else if (config.primary_feedback_type === 'testimonial') {
				responseData = {
					testimonialText,
					consentGiven: testimonialConsent,
					customAnswers: selectedRating
						? {
								rating: {
									questionText: 'Overall Rating',
									answer: String(selectedRating),
									type: 'scale',
								},
						  }
						: {},
				}
				score = selectedRating
			}

			// Create the payload
			const payload = {
				widgetId: config.id,
				teamId: config.team_id,
				programId: config.program_id,
				score,
				responseData,
				userMetadata,
				submissionType: config.primary_feedback_type,
				consentGiven:
					config.primary_feedback_type === 'testimonial'
						? testimonialConsent
						: false,
			}

			// Submit the feedback to the server
			const response = await fetch(`${backendUrl}/api/survey`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(payload),
			})

			if (!response.ok) {
				const errorBody = await response.json().catch(() => ({}))
				throw new Error(
					`API Error: ${response.status} - ${
						errorBody.error || 'Unknown error'
					}`
				)
			}

			// Survey submitted successfully
			setLastSubmissionType('feedback')
			setViewMode('thankYou')
		} catch (error) {
			// Error handling
		} finally {
			setIsSubmittingFeature(false)
		}
	}

	const handleBugSubmit = async (): Promise<void> => {
		if (bugDescription.trim().length < 10) {
			setBugSubmitError('Desc. required (10+ chars).')
			return
		}
		if (!config.id || !config.team_id) {
			setBugSubmitError('Config incomplete.')
			return
		}

		setIsSubmittingBug(true)
		setBugSubmitError(null)

		try {
			await submitBugReport({
				widgetId: config.id,
				teamId: config.team_id,
				description: bugDescription,
				screenshot: screenshotFile,
				reporterName: bugReporterName.trim() || undefined, // Add name
				reporterEmail: bugReporterEmail.trim() || undefined, // Add email
			})

			setLastSubmissionType('bug')
			setViewMode('thankYou')
		} catch (error) {
			setBugSubmitError('Failed to submit. Please try again.')
		} finally {
			setIsSubmittingBug(false)
		}
	}

	const handleContactSubmit = async (): Promise<void> => {
		if (contactMessage.trim().length < 10) {
			setContactSubmitError('Message required (10+ chars).')
			return
		}
		if (!config.id || !config.team_id) {
			setContactSubmitError('Config incomplete.')
			return
		}

		setIsSubmittingContact(true)
		setContactSubmitError(null)

		try {
			await submitContactMessage({
				widgetId: config.id,
				teamId: config.team_id,
				description: contactMessage,
				screenshot: screenshotFile,
				reporterName: contactSenderName.trim() || undefined,
				reporterEmail: contactSenderEmail.trim() || undefined,
			})

			setLastSubmissionType('contact')
			setViewMode('thankYou')
		} catch (error) {
			setContactSubmitError('Failed to submit. Please try again.')
		} finally {
			setIsSubmittingContact(false)
		}
	}

	const handleFeatureSubmit = async (): Promise<void> => {
		if (newFeatureTitle.trim().length < 5) {
			setFeatureSubmitError('Title is required (5+ chars).')
			return
		}
		if (newFeatureDescription.trim().length < 10) {
			setFeatureSubmitError('Description is required (10+ chars).')
			return
		}
		if (!config.id || !config.team_id) {
			setFeatureSubmitError('Config incomplete.')
			return
		}

		setIsSubmittingFeature(true)
		setFeatureSubmitError(null)

		try {
			await submitFeatureRequest({
				widgetId: config.id,
				teamId: config.team_id,
				programId: config.program_id, // Let backend handle program_id if not provided
				title: newFeatureTitle,
				description: newFeatureDescription,
				categoryId: newFeatureCategory || undefined,
				submitterName: submitterName.trim() || undefined, // Add name
				submitterEmail: submitterEmail.trim() || undefined, // Add email
				// You can still use submitterIdentifier if needed for anonymous tracking
			})

			// Reset form state, including new fields
			setNewFeatureTitle('')
			setNewFeatureDescription('')
			setSubmitterName('')
			setSubmitterEmail('')

			setLastSubmissionType('feature')
			setViewMode('thankYou')
		} catch (error) {
			setFeatureSubmitError('Failed to submit. Please try again.')
		} finally {
			setIsSubmittingFeature(false)
		}
	}

	// --- Bug Reporting Functions (Ensure these are present) ---
	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		const setCurrentFormError = (message: string | null) => {
			if (viewMode === 'contactForm') {
				setContactSubmitError(message)
			} else {
				setBugSubmitError(message)
			}
		}
		if (file) {
			// Validation
			if (!['image/png', 'image/jpeg'].includes(file.type)) {
				setCurrentFormError('Please select a PNG or JPG image.')
				setScreenshotFile(null)
				setScreenshotPreview(null)
				if (fileInputRef.current) fileInputRef.current.value = ''
				return
			}
			if (file.size > 5 * 1024 * 1024) {
				// 5MB limit
				setCurrentFormError('Image size cannot exceed 5MB.')
				setScreenshotFile(null)
				setScreenshotPreview(null)
				if (fileInputRef.current) fileInputRef.current.value = ''
				return
			}
			// Set state and generate preview
			setScreenshotFile(file)
			setCurrentFormError(null)
			const reader = new FileReader()
			reader.onloadend = () => {
				setScreenshotPreview(reader.result as string)
			}
			reader.readAsDataURL(file)
		}
	}

	const removeScreenshot = () => {
		setScreenshotFile(null)
		setScreenshotPreview(null)
		setBugSubmitError(null)
		if (fileInputRef.current) {
			fileInputRef.current.value = ''
		}
	}
	// --- End Bug Reporting Functions ---

	// --- Rendering Helper Functions (Existing adapted + New ones) ---

	// Corrected renderNPSScale
	const renderNPSScale = (): React.ReactNode => (
		<div className="space-y-3">
			<div className="flex justify-center gap-1.5">
				{[...Array(11)].map((_, score) => {
					const isSelected = selectedRating === score
					let bgColor = 'bg-white dark:bg-gray-700'
					let textColor = config.textColor || '#000000'
					let ringColor = 'ring-gray-300 dark:ring-gray-600'

					if (score >= 0 && score <= 6) {
						// Detractors
						bgColor = isSelected
							? 'bg-red-500 dark:bg-red-600'
							: 'bg-red-100 dark:bg-red-900/50'
						textColor = isSelected
							? 'text-white'
							: 'text-red-700 dark:text-red-300'
						ringColor = 'ring-red-500 dark:ring-red-600'
					} else if (score >= 7 && score <= 8) {
						// Passives
						bgColor = isSelected
							? 'bg-yellow-500 dark:bg-yellow-600'
							: 'bg-yellow-100 dark:bg-yellow-900/50'
						textColor = isSelected
							? 'text-white' // Use white text on darker yellow
							: 'text-yellow-700 dark:text-yellow-300'
						ringColor = 'ring-yellow-500 dark:ring-yellow-600'
					} else {
						// Promoters
						bgColor = isSelected
							? 'bg-green-500 dark:bg-green-600'
							: 'bg-green-100 dark:bg-green-900/50'
						textColor = isSelected
							? 'text-white'
							: 'text-green-700 dark:text-green-300'
						ringColor = 'ring-green-500 dark:ring-green-600'
					}

					return (
						<button
							type="button"
							key={score}
							onClick={() => setSelectedRating(score)}
							className={cn(
								'w-8 h-8 rounded-md text-sm font-medium flex items-center justify-center',
								'border dark:border-gray-600/50', // Subtle border for definition
								'transition-all duration-200 ease-in-out',
								'hover:scale-110 hover:shadow-md',
								isSelected
									? `ring-2 ring-offset-1 ${ringColor} scale-110 shadow-lg`
									: '',
								bgColor,
								textColor
							)}
							aria-pressed={isSelected}>
							{score}
						</button>
					)
				})}
			</div>
		<div
			className="flex justify-between text-xs px-1"
			style={{ color: `${config.textColor}b3` }} // Muted text color
		>
			<span>0 - {t('not_likely')}</span>
			<span>10 - {t('very_likely')}</span>
		</div>
		</div>
	)

	// Corrected renderCSATScale
	const renderCSATScale = (): React.ReactNode => {
		const ratings = [
			{
				value: 1,
				icon: Frown,
				color: 'text-red-500',
				hoverColor: 'hover:bg-red-100',
				selectedColor: 'bg-red-500 text-white ring-red-500',
				label: 'Very Unsatisfied',
			},
			{
				value: 2,
				icon: Frown,
				color: 'text-orange-500',
				hoverColor: 'hover:bg-orange-100',
				selectedColor: 'bg-orange-500 text-white ring-orange-500',
				label: 'Unsatisfied',
			},
			{
				value: 3,
				icon: Meh,
				color: 'text-yellow-500',
				hoverColor: 'hover:bg-yellow-100',
				selectedColor: 'bg-yellow-500 text-white ring-yellow-500',
				label: 'Neutral',
			},
			{
				value: 4,
				icon: Smile,
				color: 'text-lime-500',
				hoverColor: 'hover:bg-lime-100',
				selectedColor: 'bg-lime-500 text-white ring-lime-500',
				label: 'Satisfied',
			},
			{
				value: 5,
				icon: Laugh,
				color: 'text-green-500',
				hoverColor: 'hover:bg-green-100',
				selectedColor: 'bg-green-500 text-white ring-green-500',
				label: 'Very Satisfied',
			},
		]

		return (
			<div className="space-y-3">
				<div className="flex justify-between items-center gap-2 px-1">
					{ratings.map((rating) => {
						const Icon = rating.icon
						const isSelected = selectedRating === rating.value
						return (
							<span
								key={rating.value}
								className="flex flex-col items-center flex-1 group">
								<button
									type="button"
									onClick={() => setSelectedRating(rating.value)}
									className={cn(
										'w-10 h-10 sm:w-12 sm:h-12 rounded-full border flex items-center justify-center',
										'transition-all duration-200 ease-in-out transform',
										isSelected
											? `${rating.selectedColor} ring-2 ring-offset-2 scale-110 shadow-lg`
											: `bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 ${rating.color} ${rating.hoverColor} hover:scale-105 hover:shadow-sm dark:hover:bg-gray-600`
									)}
									aria-pressed={isSelected}
									aria-label={rating.label}>
									<Icon
										className={`h-5 w-5 sm:h-6 sm:w-6 ${
											isSelected ? 'text-white' : ''
										}`}
									/>
								</button>
								{/* Optional: Show labels below icons on larger screens */}
								{/* <span className={cn(
									"mt-1.5 text-xs text-center transition-opacity duration-200",
									isSelected ? 'text-gray-800 dark:text-white font-medium' : 'text-gray-500 dark:text-gray-400 group-hover:opacity-100 opacity-0 sm:opacity-100'
								)}>
									{rating.value}
								</span> */}
							</span>
						)
					})}
				</div>
				{/* Labels below the scale */}
				<div
					className="flex justify-between text-xs px-1"
					style={{ color: `${config.textColor}b3` }}>
					<span>{ratings[0].label}</span>
					<span>{ratings[4].label}</span>
				</div>
			</div>
		)
	}

	// Enhanced renderCustomQuestion UI
	const renderCustomQuestion = (question: {
		id: string
		type: 'text' | 'email' | 'multiple_choice' | 'scale'
		question: string
		required?: boolean
		options?: string[]
		order_index?: number
		min_scale?: number
		max_scale?: number
	}): React.ReactNode => {
		return (
			<div key={question.id} className="space-y-3">
				{/* Question Label with Required Indicator */}
				<Label
					htmlFor={question.id}
					className="block text-sm font-medium mb-1"
					style={{ color: config.textColor || '#000' }}>
					{question.question}
					{question.required && <span className="text-red-500 ml-1">*</span>}
				</Label>

				{/* Input Rendering based on type */}
				{question.type === 'text' && (
					<Textarea
						id={question.id}
						value={surveyAnswers[question.id] || ''}
						onChange={(e) => {
							setSurveyAnswers((prev) => ({
								...prev,
								[question.id]: e.target.value,
					}))
				}}
				placeholder={t('your_answer')}
				required={question.required}
				className="w-full rounded-lg border bg-white p-3 text-sm shadow-sm focus:border-transparent focus:ring-2 focus:ring-offset-1 dark:bg-gray-800 dark:border-gray-700 dark:focus:ring-offset-gray-900"
						style={
							{
								borderColor: `${config.primaryColor}66`,
								'--tw-ring-color': config.primaryColor,
								color: config.textColor || '#000',
								backgroundColor: getInputBackgroundColor(),
							} as React.CSSProperties
						}
						rows={3}
					/>
				)}

				{question.type === 'email' && (
					<Input
						id={question.id}
						type="email"
						value={surveyAnswers[question.id] || ''}
						onChange={(e) => {
							setSurveyAnswers((prev) => ({
								...prev,
								[question.id]: e.target.value,
				}))
			}}
			placeholder={t('email_placeholder')}
			required={question.required}
			className="w-full rounded-lg border bg-white p-3 text-sm shadow-sm focus:border-transparent focus:ring-2 focus:ring-offset-1 dark:bg-gray-800 dark:border-gray-700 dark:focus:ring-offset-gray-900"
						style={
							{
								borderColor: `${config.primaryColor}66`,
								'--tw-ring-color': config.primaryColor,
								color: config.textColor || '#000',
								backgroundColor: getInputBackgroundColor(),
							} as React.CSSProperties
						}
					/>
				)}

				{question.type === 'multiple_choice' && (
					<RadioGroup
						value={surveyAnswers[question.id] || ''}
						onValueChange={(value) =>
							setSurveyAnswers((prev) => ({ ...prev, [question.id]: value }))
						}
						required={question.required}
						className="space-y-2"
						aria-label={question.question}>
						{question.options?.map((option, index) => (
							<Label
								key={index}
								htmlFor={`${question.id}-${index}`}
								className={cn(
									'flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-all duration-200 ease-in-out',
									'hover:bg-gray-50 dark:hover:bg-gray-700/50',
									surveyAnswers[question.id] === option
										? 'border-transparent ring-2 ring-offset-1 dark:ring-offset-gray-900 shadow-md'
										: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
								)}
								style={
									surveyAnswers[question.id] === option
										? ({
												'--tw-ring-color': config.primaryColor,
												backgroundColor: `${config.primaryColor}1A`,
												borderColor: config.primaryColor,
												color: config.textColor,
										  } as React.CSSProperties)
										: {
												color: config.textColor,
												backgroundColor: getInputBackgroundColor(),
												borderColor: `${config.textColor}33`,
										  }
								}>
								<RadioGroupItem
									value={option}
									id={`${question.id}-${index}`}
									className="border-gray-300 dark:border-gray-600"
									style={
										surveyAnswers[question.id] === option
											? {
													borderColor: config.primaryColor,
													backgroundColor: config.primaryColor,
											  }
											: {}
									}
								/>
								<span className="text-sm">{option}</span>
							</Label>
						))}
					</RadioGroup>
				)}

				{question.type === 'scale' && (
					<div className="space-y-3">
						<div className="flex flex-wrap justify-center gap-1.5 sm:gap-2">
							{Array.from(
								{
									length:
										(question.max_scale ?? 5) - (question.min_scale ?? 1) + 1,
								},
								(_, i) => i + (question.min_scale ?? 1)
							).map((value) => {
								const isSelected = surveyAnswers[question.id] === String(value)
								return (
									<button
										key={value}
										type="button"
										onClick={() =>
											setSurveyAnswers((prev) => ({
												...prev,
												[question.id]: String(value),
											}))
										}
										className={cn(
											'w-9 h-9 sm:w-10 sm:h-10 rounded-lg text-sm font-medium flex items-center justify-center border',
											'transition-all duration-200 ease-in-out',
											'focus:outline-none focus:ring-2 focus:ring-offset-1 dark:focus:ring-offset-gray-900',
											isSelected
												? 'text-white scale-110 shadow-md'
												: 'hover:scale-105 hover:shadow-sm'
										)}
										style={
											isSelected
												? ({
														backgroundColor: config.primaryColor,
														borderColor: config.primaryColor,
														color: '#FFFFFF',
														'--tw-ring-color': config.primaryColor,
												  } as React.CSSProperties)
												: ({
														backgroundColor: getInputBackgroundColor(),
														borderColor: `${config.textColor}33`,
														color: config.textColor,
														'--tw-ring-color': config.primaryColor,
												  } as React.CSSProperties)
										}
										aria-pressed={isSelected}>
										{value}
									</button>
								)
							})}
						</div>
						{/* Descriptive Labels */}
						<div
							className="flex justify-between text-xs px-1"
							style={{ color: `${config.textColor}b3` }}>
							<span>{question.min_scale ?? 1} - Low</span>
							<span>{question.max_scale ?? 5} - High</span>
						</div>
					</div>
				)}
			</div>
		)
	}

	// Updated renderBranding styles
	const renderBranding = (): React.ReactNode => {
		if (config.showBranding === false) return null
		return (
			<>
				<div
					className="h-px"
					style={{
						backgroundColor:
							config.brandingStyle?.borderColor || getBorderColor(),
					}}
				/>
				<div
					className="p-3 text-right text-xs"
					style={{
						color: isGlassmorphismEnabled()
							? config.textColor + '80'
							: '#6B7280',
					}}>
					Powered by{' '}
					<span className="font-medium" style={{ color: '#4461F2' }}>
						Feedbask
					</span>
				</div>
			</>
		)
	}

	// Render the floating action button
	const renderFeedbackButton = (): React.ReactNode => {
		const position = config.position || 'bottom-right'
		const isWallMounted =
			position === 'middle-left' || position === 'middle-right'
		const isBottomCenterMounted = position === 'bottom-center'
		const isEdgeMounted = isWallMounted || isBottomCenterMounted
		const triggerSize: 's' | 'm' | 'l' = config.widgetTriggerSize || 'm'
		const triggerSizeClasses = {
			s: 'text-xs',
			m: 'text-sm',
			l: 'text-base',
		}
		const defaultButtonSizeClasses = {
			s: 'px-3 py-1.5',
			m: 'px-4 py-2',
			l: 'px-5 py-3',
		}
		const edgeButtonSizeClasses = {
			s: 'px-1.5 py-3.5',
			m: 'px-2 py-4',
			l: 'px-2.5 py-5',
		}
		const iconSizeClasses = {
			s: 'h-3.5 w-3.5',
			m: 'h-4 w-4',
			l: 'h-5 w-5',
		}

		return (
			<motion.button
				onClick={() => setIsOpen(true)}
				className={cn(
					'fixed z-50 flex items-center gap-2 shadow-lg transition-transform',
					'hover:scale-105 active:scale-95',
					triggerSizeClasses[triggerSize],
					isEdgeMounted
						? position === 'middle-left'
							? `${edgeButtonSizeClasses[triggerSize]} justify-center rounded-r-2xl rounded-l-none`
							: position === 'middle-right'
							? `${edgeButtonSizeClasses[triggerSize]} justify-center rounded-l-2xl rounded-r-none`
							: `${defaultButtonSizeClasses[triggerSize]} justify-center rounded-t-2xl rounded-b-none border-0`
						: `${defaultButtonSizeClasses[triggerSize]} rounded-full`
				)}
				style={{
					backgroundColor: feedbackButton.color,
					color: feedbackButton.textColor,
					...getButtonPosition(),
					...(isWallMounted && {
						writingMode: 'vertical-lr' as const,
						textOrientation: 'mixed' as const,
						transform: getButtonPosition().transform,
					}),
					...(!isEdgeMounted && {
						borderRadius: '9999px', // rounded-full
					}),
				}}
				initial={{ scale: 0.9, opacity: 0 }}
				animate={{ scale: 1, opacity: 1 }}
				exit={{ scale: 0.9, opacity: 0 }}>
				{!isEdgeMounted &&
					(config.widgetTriggerIcon ? (
						<img
							src={config.widgetTriggerIcon}
							alt="Widget trigger icon"
							className={cn('rounded-sm object-contain', iconSizeClasses[triggerSize])}
						/>
					) : (
						<MessageSquare className={iconSizeClasses[triggerSize]} />
					))}
				<span>{feedbackButton.text}</span>
			</motion.button>
		)
	}

	// ===== Feedback Card Component =====
	const renderFeedbackCard = (): React.ReactNode => {
		// Use sidebar state from component level (already defined at top)

		// Calculate sidebar width offset for left-positioned widgets
		const getSidebarOffset = () => {
			if (isMobile) return 0 // No sidebar on mobile
			if (sidebarState === 'expanded') return 224 // 14rem = 224px
			if (sidebarState === 'collapsed') return 48 // 3rem = 48px
			return 0
		}

		// Calculate card position based on the widget position
		const cardStyle: React.CSSProperties = {
			position: 'fixed' as const,
			zIndex: 999999,
			animationName: 'none',
			animationDuration: '0.3s',
			animationTimingFunction: 'ease-out',
			animationFillMode: 'forwards',
		}
		if (config.position === 'bottom-right') {
			cardStyle.bottom = '20px'
			cardStyle.right = '20px'
			cardStyle.animationName = 'slideUp'
		}
		if (config.position === 'bottom-left') {
			cardStyle.bottom = '20px'
			cardStyle.left = `${getSidebarOffset() + 20}px`
			cardStyle.animationName = 'slideUp'
		}
		if (config.position === 'bottom-center') {
			cardStyle.bottom = '20px'
			cardStyle.left = '50%'
			cardStyle.transform = 'translateX(-50%)'
			cardStyle.animationName = 'slideUpCenter'
		}
		if (config.position === 'middle-left') {
			cardStyle.top = '50%'
			cardStyle.left = `${getSidebarOffset() + 20}px`
			cardStyle.transform = 'translateY(-50%)'
			cardStyle.animationName = 'slideRight'
		}
		if (config.position === 'middle-right') {
			cardStyle.top = '50%'
			cardStyle.right = '20px'
			cardStyle.transform = 'translateY(-50%)'
			cardStyle.animationName = 'slideLeft'
		}

		let bodyContent: ReactNode
		switch (viewMode) {
			case 'mainMenu':
				bodyContent = renderMainMenu()
				break
			case 'bugForm':
				bodyContent = renderBugForm()
				break
			case 'contactForm':
				bodyContent = renderContactForm()
				break
			case 'featureList':
				bodyContent = renderFeatureList()
				break
			case 'featureForm':
				bodyContent = renderFeatureForm()
				break
			case 'surveyStep':
			case 'testimonialForm':
				bodyContent = renderPrimaryFeedbackFlow()
				break
			case 'thankYou':
				bodyContent = renderThankYou()
				break
			default:
				bodyContent = renderMainMenu() // Fallback
		}

		return (
			<>
				<style>
					{`
					@keyframes slideUp {
						from { transform: translateY(30px); opacity: 0; }
						to { transform: translateY(0); opacity: 1; }
					}
					
					@keyframes slideUpCenter {
						from { transform: translateY(30px) translateX(-50%); opacity: 0; }
						to { transform: translateY(0) translateX(-50%); opacity: 1; }
					}
					
					@keyframes slideRight {
						from { transform: translateX(-30px) translateY(-50%); opacity: 0; }
						to { transform: translateX(0) translateY(-50%); opacity: 1; }
					}
					
					@keyframes slideLeft {
						from { transform: translateX(30px) translateY(-50%); opacity: 0; }
						to { transform: translateX(0) translateY(-50%); opacity: 1; }
					}
					`}
				</style>
				<Card
					className="w-[400px] shadow-lg rounded-xl border-none overflow-hidden flex flex-col max-h-[90vh]"
					style={{ backgroundColor: config.backgroundColor }}>
					<WidgetHeader />
					<CardContent className="p-0 flex-grow overflow-y-auto">
						<AnimatePresence mode="wait">
							<motion.div
								key={viewMode}
								variants={contentVariants}
								initial="enter"
								animate="center"
								exit="exit"
								transition={{ duration: 0.2 }}>
								{viewMode === 'mainMenu' && renderMainMenu()}
								{viewMode === 'bugForm' && renderBugForm()}
								{viewMode === 'contactForm' && renderContactForm()}
								{viewMode === 'featureList' && renderFeatureList()}
								{viewMode === 'featureForm' && renderFeatureForm()}
								{viewMode === 'surveyStep' && renderPrimaryFeedbackFlow()}
								{viewMode === 'testimonialForm' && renderPrimaryFeedbackFlow()}
								{viewMode === 'reviewForm' && renderReviewForm()}
								{viewMode === 'thankYou' && renderThankYou()}
								{viewMode === 'chatInit' && renderChatInit()}
								{viewMode === 'chatView' && renderChatView()}
							</motion.div>
						</AnimatePresence>
					</CardContent>
					{renderBranding()}
				</Card>
			</>
		)
	}

	// --- Main Component Structure ---

	// Updated WidgetHeader to match target UI
	const WidgetHeader = (): React.ReactNode => {
		// Dynamic gradient style
		const hasGradient =
			config.gradientEndColor && config.gradientEndColor !== config.primaryColor

		const headerStyle = config.layout?.headerBackground
			? {
					backgroundColor: config.layout.headerBackground,
			  }
			: hasGradient
			? {
					background: isGlassmorphismEnabled()
						? `linear-gradient(to right, ${
								config.primaryColor || '#0066FF'
						  }ee, ${config.gradientEndColor}ee)`
						: `linear-gradient(to bottom right, ${
								config.primaryColor || '#0066FF'
						  }, ${config.gradientEndColor})`,
			  }
			: {
					backgroundColor: isGlassmorphismEnabled()
						? (config.primaryColor || '#0066FF') + 'ee'
						: config.primaryColor || '#0066FF',
			  }

		// Animation variants for gradient shift
		const gradientAnimation = {
			'subtle-gradient-shift': {
				background: [
					`linear-gradient(135deg, ${config.primaryColor || '#0066FF'}, ${
						config.gradientEndColor || config.primaryColor
					})`,
					`linear-gradient(135deg, ${
						config.gradientEndColor || config.primaryColor
					}, ${config.primaryColor || '#0066FF'})`,
					`linear-gradient(135deg, ${config.primaryColor || '#0066FF'}, ${
						config.gradientEndColor || config.primaryColor
					})`,
				],
				transition: {
					duration: 10,
					repeat: Infinity,
					ease: 'easeInOut' as const,
					repeatType: 'loop' as const,
				},
			},
			none: {},
		}

		const shouldAnimate =
			config.animation?.header === 'subtle-gradient-shift' && hasGradient

		const trimmedHeaderTitle = config.header_title?.trim()
		const shouldRenderHeader = trimmedHeaderTitle !== ''
		const previewHeaderTitle = trimmedHeaderTitle || t('menu_title')

		return (
			<motion.div
				className={cn(
					config.layout?.headerPadding || 'p-5 pt-12',
					'text-left relative',
					isGlassmorphismEnabled() && 'backdrop-blur-sm'
				)}
				style={{
					...headerStyle,
					color: config.layout?.headerTextColor || 'white',
					position: 'relative',
				}}
				animate={
					shouldAnimate ? gradientAnimation['subtle-gradient-shift'] : {}
				}>
				{isGlassmorphismEnabled() && (
					<div className="absolute inset-0 bg-white/10" />
				)}
				{viewMode !== 'mainMenu' && viewMode !== 'thankYou' && (
					<Button
						type="button"
						variant="ghost"
						size="icon"
						onClick={() => setViewMode('mainMenu')}
						className="absolute top-3 left-3 h-7 w-7 rounded-full hover:bg-white/20 z-10"
						aria-label="Go back">
						<ArrowLeft className="h-4 w-4" />
					</Button>
				)}
				<Button
					type="button"
					variant="ghost"
					size="icon"
					onClick={() => resetWidget()}
					className="absolute top-3 right-3 h-7 w-7 rounded-full hover:bg-white/20 z-10"
					aria-label="Close widget">
					<X className="h-4 w-4" />
				</Button>
				{/* Show header content unless explicitly hidden with empty string */}
				{shouldRenderHeader ? (
					<div className="relative z-10">
						<h2 className="text-base mb-0.5">{t('hi_greeting')}</h2>
						<p className="text-xl font-medium">{previewHeaderTitle}</p>
					</div>
				) : null}
			</motion.div>
		)
	}

	// Updated renderMainMenu to match target UI
	const renderMainMenu = (): React.ReactNode => {
		const isCardLayout = config.menuItemStyle?.layout === 'separate'
		const isGridTwoColumns = config.layout?.menuColumns === 2

		// Check if there's an active chat conversation to resume
		const hasActiveChat = chatConversationId && chatMessages.length > 0

		const MenuItem = ({
			title,
			description,
			icon: Icon,
			targetView,
			iconColor = '#4461F2',
			bgColor = '#EEF1FF',
			statusBadge, // New prop for status badge
		}: {
			title: string
			description: string
			icon: React.ElementType
			targetView: ViewMode
			iconColor?: string
			bgColor?: string
			statusBadge?: React.ReactNode // New prop for status badge
		}) => (
			<button
				type="button"
				onClick={() => navigateTo(targetView)}
				className={cn(
					'group w-full p-4 transition-all duration-300',
					isGridTwoColumns ? 'min-h-[110px] justify-center' : '',
					config.menuItemStyle?.verticalAlign
						? 'flex items-center gap-4 text-left'
						: config.menuItemStyle?.centerAlign
						? 'flex items-center justify-center gap-3 text-center'
						: 'flex items-start gap-4 text-left',
					isGridTwoColumns && 'flex-col items-center text-center gap-2',
					isCardLayout
						? (config.menuItemStyle?.useBorder || config.layout?.containerBorder)
							? 'rounded-2xl border'
							: 'rounded-2xl shadow-sm hover:shadow-md'
						: config.layout?.containerBorder
						? 'rounded-2xl border hover:bg-white/10'
						: 'rounded-2xl hover:bg-white/10',
					isGlassmorphismEnabled() &&
						'border border-white/10 hover:border-white/20 backdrop-blur-sm'
				)}
				style={{
					color: config.menuItemStyle?.textColor || config.textColor || '#111',
					backgroundColor: isCardLayout
						? config.menuItemStyle?.background || '#FFFFFF'
						: isGlassmorphismEnabled()
						? 'rgba(255, 255, 255, 0.05)'
						: 'transparent',
					borderColor: (config.menuItemStyle?.useBorder || config.layout?.containerBorder)
						? config.menuItemStyle?.borderColor || '#E5E7EB'
						: undefined,
				}}>
				<div
					className={cn(
						'p-2.5 rounded-full flex-shrink-0 transition-transform duration-300 group-hover:scale-110',
						isGlassmorphismEnabled() && 'backdrop-blur-sm border'
					)}
					style={{
						background: isGlassmorphismEnabled()
							? `linear-gradient(135deg, ${bgColor}, ${bgColor}dd)`
							: bgColor,
						borderColor: isGlassmorphismEnabled()
							? iconColor + '30'
							: 'transparent',
					}}>
					<Icon className="h-5 w-5" style={{ color: iconColor }} />
				</div>
				<div
					className={cn(
						isGridTwoColumns && 'flex justify-center',
						config.menuItemStyle?.centerAlign ? 'text-center' : 'flex-1'
					)}>
					<div className="flex items-center justify-between">
						<div>
							<div className="text-base font-medium mb-0.5">{title}</div>
							{config.menuItemStyle?.showDescriptions !== false && (
								<div className="text-sm opacity-70">{description}</div>
							)}
						</div>
						{statusBadge && <div className="ml-2">{statusBadge}</div>}
					</div>
				</div>
			</button>
		)

		// Component for Live Chat menu item with status badge
		const LiveChatMenuItem = ({
			chatConversationId,
			programId,
			config,
		}: {
			chatConversationId: string | null
			programId: string
			config: WidgetConfig
		}) => {
			const [availability, setAvailability] = useState<{
				available: boolean
				online_agents: number
				agents?: Array<{
					user_id: string
					full_name: string
					job_title?: string
					avatar_url?: string
					status: 'online' | 'away' | 'offline'
				}>
			} | null>(null)
			const [loading, setLoading] = useState(true)

			useEffect(() => {
				const checkAvailability = async () => {
					try {
						const { getAgentAvailability } = await import('@/lib/api')
						const data = await getAgentAvailability(programId)
						setAvailability({
							available: data.available,
							online_agents: data.online_agents,
							agents: data.agents || [],
						})
					} catch (error) {
						// Error handling
					} finally {
						setLoading(false)
					}
				}

				checkAvailability()

				// Check availability every 30 seconds
				const interval = setInterval(checkAvailability, 30000)

				return () => clearInterval(interval)
			}, [programId])

			const statusBadge = loading ? (
				<div className="flex items-center gap-1.5">
					<div className="h-2 w-2 rounded-full bg-gray-400 animate-pulse"></div>
					<span className="text-xs text-gray-500">Checking...</span>
				</div>
			) : availability ? (
				availability.available &&
				availability.agents &&
				availability.agents.length > 0 ? (
					<div className="flex items-center gap-2">
						{/* Show up to 2 agent avatars */}
						<div className="flex -space-x-2">
							{availability.agents.slice(0, 2).map((agent, index) => (
								<div
									key={agent.user_id}
									className="relative"
									style={{ zIndex: 2 - index }}>
									{agent.avatar_url ? (
										<img
											src={agent.avatar_url}
											alt={agent.full_name}
											className="h-6 w-6 rounded-full border-2 border-white object-cover"
										/>
									) : (
										<div
											className="h-6 w-6 rounded-full border-2 border-white flex items-center justify-center text-xs font-medium text-white"
											style={{
												backgroundColor: config.primaryColor || '#8B5CF6',
											}}>
											{agent.full_name.charAt(0).toUpperCase()}
										</div>
									)}
									{index === 0 && (
										<div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-green-500 border border-white"></div>
									)}
								</div>
							))}
							{availability.agents.length > 2 && (
								<div className="h-6 w-6 rounded-full border-2 border-white flex items-center justify-center text-xs font-medium bg-gray-200 text-gray-600">
									+{availability.agents.length - 2}
								</div>
							)}
						</div>
						<div className="text-xs">
							{availability.agents.length === 1 ? (
								<span className="text-green-600 font-medium">
									{availability.agents[0].full_name.split(' ')[0]} is here
								</span>
							) : (
								<span className="text-green-600 font-medium">
									{availability.online_agents} agents online
								</span>
							)}
						</div>
					</div>
				) : (
				<div className="flex items-center gap-1.5">
					<div className="h-2 w-2 rounded-full bg-gray-400"></div>
					<span className="text-xs text-gray-500">{t('offline')}</span>
				</div>
				)
			) : null

			return (
				<MenuItem
					title="Live Chat"
					description={
						chatConversationId
							? 'Continue conversation'
							: availability?.available
							? 'Chat with our support team'
							: 'Leave a message for our team'
					}
					icon={MessageCircle}
					targetView={chatConversationId ? 'chatView' : 'chatInit'}
					iconColor="#8B5CF6"
					bgColor="#F3E8FF"
					statusBadge={statusBadge}
				/>
			)
		}

		return (
			<div
				className={cn(
					isGridTwoColumns ? 'grid grid-cols-2 gap-3' : '',
					getMenuPadding(),
					!isGridTwoColumns && getItemSpacing()
				)}>

			{config.enable_survey && (
				<MenuItem
					title={t('feedback')}
					description={t('feedback_desc')}
					icon={Star}
					targetView="surveyStep"
					iconColor={config.menuItemColors?.feedback_icon || '#F59E0B'}
					bgColor={config.menuItemColors?.feedback_bg || '#FEF3C7'}
				/>
			)}
			{config.enable_reviews && (
				<MenuItem
					title={t('reviews')}
					description={t('reviews_desc')}
					icon={MessageSquare}
					targetView="reviewForm"
					iconColor={config.menuItemColors?.review_icon || '#6366F1'}
					bgColor={config.menuItemColors?.review_bg || '#EEF2FF'}
				/>
			)}
			{config.enable_bug_reporting && (
				<MenuItem
					title={t('bug_report')}
					description={t('bug_desc')}
					icon={Bug}
					targetView="bugForm"
					iconColor={config.menuItemColors?.bug_icon || '#DC2626'}
					bgColor={config.menuItemColors?.bug_bg || '#FEE2E2'}
				/>
			)}
			{config.enable_contact_messages && (
				<MenuItem
					title={t('contact_message')}
					description={t('contact_desc')}
					icon={Mail}
					targetView="contactForm"
					iconColor={config.menuItemColors?.contact_icon || '#0EA5E9'}
					bgColor={config.menuItemColors?.contact_bg || '#E0F2FE'}
				/>
			)}
			{config.enable_feature_requests && (
				<MenuItem
					title={t('feature_request')}
					description={t('feature_desc')}
					icon={LightbulbIcon}
					targetView="featureList"
					iconColor={config.menuItemColors?.feature_icon || '#059669'}
					bgColor={config.menuItemColors?.feature_bg || '#D1FAE5'}
				/>
			)}
			{config.enable_live_chat && (
				<div className="space-y-2">
					{config.program_id ? (
						<LiveChatMenuItem
							chatConversationId={chatConversationId}
							programId={config.program_id}
							config={config}
						/>
					) : (
						<MenuItem
							title={t('live_chat')}
							description={t('live_chat_desc')}
							icon={MessageCircle}
							targetView={chatConversationId ? 'chatView' : 'chatInit'}
							iconColor="#8B5CF6"
							bgColor="#F3E8FF"
						/>
					)}
				</div>
			)}
			</div>
		)
	}

	const renderBottomModuleNavigation = (): React.ReactNode => {
		if (!shouldShowBottomModuleNav()) return null

		const modules = getBottomModules()
		const activeColor = config.primaryColor || '#4461F2'
		const inactiveColor = (config.textColor || '#111827') + '99'

		return (
			<div
				className="border-t px-2 py-2"
				style={{
					borderColor: getBorderColor('#E5E7EB'),
					backgroundColor: isGlassmorphismEnabled()
						? 'rgba(255, 255, 255, 0.75)'
						: config.backgroundColor || '#FFFFFF',
					backdropFilter: isGlassmorphismEnabled()
						? `blur(${config.glassmorphism?.backdropBlur || '16px'})`
						: undefined,
					WebkitBackdropFilter: isGlassmorphismEnabled()
						? `blur(${config.glassmorphism?.backdropBlur || '16px'})`
						: undefined,
				}}>
				<div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap pb-1">
					{modules.map((moduleItem) => {
						const isActive = viewMode === moduleItem.targetView
						const Icon = moduleItem.icon

						return (
							<button
								key={moduleItem.id}
								type="button"
								onClick={() => navigateTo(moduleItem.targetView)}
								className="min-w-[120px] rounded-xl px-3 py-2 flex flex-col items-center justify-center gap-1 transition-all flex-shrink-0"
								style={{
									color: isActive ? activeColor : inactiveColor,
									backgroundColor: isActive ? `${activeColor}18` : 'transparent',
								}}>
								<Icon className="h-4 w-4" />
								<span className="text-[12px] font-medium leading-none">
									{moduleItem.label}
								</span>
							</button>
						)
					})}
				</div>
			</div>
		)
	}

	const renderPrimaryFeedbackFlow = (): React.ReactNode => {
		let content: ReactNode = null
		let title: string = config.question || 'Provide Feedback'

		switch (config.primary_feedback_type) {
			case 'nps':
			content = renderNPSScale()
			title = config.question || t('nps_question')
			break
		case 'csat':
			content = renderCSATScale()
			title = config.question || t('csat_question')
				break
			case 'custom':
				if (config.customQuestions && config.customQuestions.length > 0) {
					const currentQ = config.customQuestions[currentQuestionIndex]
					content = renderCustomQuestion(currentQ)
					title = currentQ.question
				} else {
					content = (
						<p
							className="text-center text-sm"
							style={{ color: config.textColor + 'b3' }}>
							No survey questions configured.
						</p>
					)
				}
			break
		case 'testimonial':
			title = t('reviews')
			content = (
					<div className="space-y-4">
						<div className="space-y-1">
					<Label
						className="text-sm font-medium"
						style={{ color: config.textColor }}>
						{t('overall_rating_optional')}
					</Label>
							{renderCSATScale()}
						</div>
						<div className="space-y-1">
							<Label
								htmlFor="testimonial-text"
								className="text-sm font-medium"
								style={{ color: config.textColor }}>
								Your review
							</Label>
							<Textarea
								id="testimonial-text"
								value={testimonialText}
								onChange={(e) => setTestimonialText(e.target.value)}
								rows={4}
								required
								minLength={10}
								className="bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700 rounded-md p-2 w-full text-sm focus:ring-1 focus:ring-opacity-50 focus:border-transparent"
								style={
									{
										color: '#333',
										borderColor: config.primaryColor,
										'--tw-ring-color': config.primaryColor,
							} as React.CSSProperties
						}
						placeholder={t('tell_us_what_you_think')}
					/>
						</div>
						<div className="flex items-start space-x-2">
							<input
								type="checkbox"
								id="testimonial-consent"
								checked={testimonialConsent}
								onChange={(e) => setTestimonialConsent(e.target.checked)}
								className="mt-1 form-checkbox h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
								style={{ accentColor: config.primaryColor }}
							/>
							<Label
								htmlFor="testimonial-consent"
						className="text-xs font-normal leading-snug select-none cursor-pointer"
						style={{ color: config.textColor + 'cc' }}>
						{t('i_agree_feedback')}
					</Label>
						</div>
					</div>
				)
				break
			default:
				content = (
					<p
						className="text-center text-sm"
						style={{ color: config.textColor + 'b3' }}>
						No feedback type selected.
					</p>
				)
		}

		const showProgressBar =
			config.primary_feedback_type === 'custom' &&
			config.customQuestions &&
			config.customQuestions.length > 1
		const progressPercent = showProgressBar
			? ((currentQuestionIndex + 1) / config.customQuestions!.length) * 100
			: 0
		const isLastCustomStep =
			config.primary_feedback_type === 'custom' &&
			currentQuestionIndex === (config.customQuestions?.length || 0) - 1
		const showSubmitButton =
			config.primary_feedback_type !== 'custom' || isLastCustomStep

		// Determine if we should show the Next button (only for custom questions that aren't the last one)
		const showNextButton =
			config.primary_feedback_type === 'custom' &&
			!isLastCustomStep &&
			config.customQuestions &&
			config.customQuestions.length > 0

		// Function to handle the Next button click
		const handleNextQuestion = () => {
			const currentQ = config.customQuestions?.[currentQuestionIndex]

			// Only proceed if current question is answered (if required)
			if (currentQ?.required && !surveyAnswers[currentQ.id]) {
				// Could add validation message here
				return
			}

			setCurrentQuestionIndex((prev) => prev + 1)
		}

		return (
			<div className="px-4 pb-4 pt-2 space-y-4">
				{config.primary_feedback_type !== 'custom' && (
					<h3
						className="text-center font-semibold text-base"
						style={{ color: config.textColor }}>
						{title}
					</h3>
				)}

				{showProgressBar && (
					<div
						style={{
							width: '100%',
							height: '4px',
							backgroundColor: '#e5e7eb',
							borderRadius: '2px',
							overflow: 'hidden',
							marginTop: '10px',
						}}>
						<div
							style={{
								width: `${progressPercent}%`,
								height: '100%',
								backgroundColor: config.primaryColor,
								transition: 'width 0.3s ease',
							}}
						/>
					</div>
				)}
				{content}

				<div className="flex gap-2">
					{showNextButton && (
						<Button
							type="button"
							onClick={handleNextQuestion}
							className="w-full py-2.5 mt-2"
							style={{
								backgroundColor: config.primaryColor,
								color: 'white',
							}}>
							Next
						</Button>
					)}

					{showSubmitButton && (
						<Button
							type="button"
							onClick={handlePrimaryFeedbackSubmit}
							disabled={
								(config.primary_feedback_type === 'nps' &&
									selectedRating === null) ||
								isSubmittingFeature
							}
							className="w-full py-2.5 mt-2"
							style={{
								backgroundColor: config.primaryColor,
								color: 'white',
								opacity: isSubmittingFeature ? 0.6 : 1,
							}}>
					{isSubmittingFeature ? (
						<Loader2 className="h-4 w-4 mr-2 animate-spin" />
					) : null}
					{config.buttonText || t('submit_feedback')}
				</Button>
					)}
				</div>
			</div>
		)
	}

	const renderBugForm = (): React.ReactNode => {
		return (
			<div className="px-4 pb-4">
				{/* Title moved to header */}
				<div className="space-y-4 mt-4">
					<div className="space-y-1.5">
					<Label
						htmlFor="bug-description"
						className="text-sm font-medium"
						style={{ color: config.textColor }}>
						{t('describe_issue')}
					</Label>
					<Textarea
						id="bug-description"
						placeholder={t('what_trying_to_do')}
							value={bugDescription}
							onChange={(e) => setBugDescription(e.target.value)}
							rows={4}
							className="bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700 rounded-md p-2 w-full text-sm focus:ring-1 focus:ring-opacity-50 focus:border-transparent"
							style={
								{
									color: '#333',
									borderColor: config.primaryColor,
									'--tw-ring-color': config.primaryColor,
								} as React.CSSProperties
							}
							minLength={10}
							required
						/>
					</div>
					<div className="space-y-1.5">
						<Label
							className="text-sm font-medium"
							style={{
								color: config.textColor,
								display: 'block',
							marginBottom: '4px',
						}}>
						{t('attach_screenshot_optional')}
					</Label>
						{screenshotPreview ? (
							<div
								className="relative group border border-gray-300 dark:border-gray-700 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-800"
								style={{ borderColor: config.primaryColor }}>
								<img
									src={screenshotPreview}
									alt="Preview"
									className="max-w-full h-auto max-h-32 object-contain display-block mx-auto p-1"
								/>
								<Button
									type="button"
									variant="ghost"
									size="icon"
									onClick={removeScreenshot}
									aria-label="Remove screenshot"
									className="absolute top-1 right-1 h-6 w-6 bg-black/60 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80">
									<X className="h-4 w-4" />
								</Button>
							</div>
						) : (
							<Button
								type="button"
								variant="outline"
								onClick={() => fileInputRef.current?.click()}
								className="w-full flex items-center justify-center gap-2 text-sm py-2 border-dashed hover:border-solid transition-all"
								style={{
									color: config.textColor || '#374151',
									borderColor: config.primaryColor + '60',
									backgroundColor: isGlassmorphismEnabled()
										? 'rgba(255, 255, 255, 0.1)'
										: 'transparent',
								}}>
								<ImageIcon
									className="h-4 w-4"
									style={{
										color: config.primaryColor || '#6B7280',
							}}
						/>
						{t('attach_screenshot_btn')}
					</Button>
						)}
						<input
							type="file"
							ref={fileInputRef}
							hidden
							accept="image/png, image/jpeg"
							onChange={handleFileChange}
						/>
					</div>

					{/* ---- START: Add Reporter Info ---- */}
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
				<div className="space-y-1.5">
					<Label
						htmlFor="bug-reporter-name"
						className="text-sm font-medium"
						style={{ color: config.textColor }}>
						{t('your_name_optional')}
					</Label>
							<Input
								id="bug-reporter-name"
								placeholder="Jane Doe"
								value={bugReporterName}
								onChange={(e) => setBugReporterName(e.target.value)}
								className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700"
								style={{
									color: '#333',
									backgroundColor: config.glassmorphism?.enabled
										? 'rgba(255, 255, 255, 0.95)'
										: '#FFFFFF',
								}}
							/>
						</div>
				<div className="space-y-1.5">
					<Label
						htmlFor="bug-reporter-email"
						className="text-sm font-medium"
						style={{ color: config.textColor }}>
						{t('your_email_optional')}
					</Label>
							<Input
								id="bug-reporter-email"
								type="email"
								placeholder="jane@example.com"
								value={bugReporterEmail}
								onChange={(e) => setBugReporterEmail(e.target.value)}
								className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700"
								style={{
									color: '#333',
									backgroundColor: config.glassmorphism?.enabled
										? 'rgba(255, 255, 255, 0.95)'
										: '#FFFFFF',
								}}
							/>
						</div>
				</div>
				<p className="text-xs" style={{ color: config.textColor + 'b3' }}>
					{t('providing_email_help')}
				</p>
					{/* ---- END: Add Reporter Info ---- */}

					{bugSubmitError && (
						<p className="text-sm text-red-600 font-medium">{bugSubmitError}</p>
					)}
					<Button
						type="button"
						onClick={handleBugSubmit}
						disabled={isSubmittingBug || bugDescription.trim().length < 10}
						className="w-full py-2.5 text-sm font-semibold"
						style={{
							backgroundColor: config.primaryColor,
							color: 'white',
							opacity:
								isSubmittingBug || bugDescription.trim().length < 10 ? 0.6 : 1,
						}}>
						{isSubmittingBug && (
							<Loader2 className="h-4 w-4 mr-2 animate-spin" />
						)}{' '}
						Submit Bug Report
					</Button>
				</div>
			</div>
		)
	}

const renderContactForm = (): React.ReactNode => {
		return (
			<div className="px-4 pb-4">
				{/* Title moved to header */}
				<div className="space-y-4 mt-4">
					<div className="space-y-1.5">
					<Label
						htmlFor="contact-message"
						className="text-sm font-medium"
						style={{ color: config.textColor }}>
						{t('contact_description')}
					</Label>
					<Textarea
						id="contact-message"
						placeholder={t('contact_placeholder')}
							value={contactMessage}
							onChange={(e) => setContactMessage(e.target.value)}
							rows={4}
							className="bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700 rounded-md p-2 w-full text-sm focus:ring-1 focus:ring-opacity-50 focus:border-transparent"
							style={
								{
									color: '#333',
									borderColor: config.primaryColor,
									'--tw-ring-color': config.primaryColor,
								} as React.CSSProperties
							}
							minLength={10}
							required
						/>
					</div>
					<div className="space-y-1.5">
						<Label
							className="text-sm font-medium"
							style={{
								color: config.textColor,
								display: 'block',
							marginBottom: '4px',
						}}>
						{t('attach_screenshot_optional')}
					</Label>
						{screenshotPreview ? (
							<div
								className="relative group border border-gray-300 dark:border-gray-700 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-800"
								style={{ borderColor: config.primaryColor }}>
								<img
									src={screenshotPreview}
									alt="Preview"
									className="max-w-full h-auto max-h-32 object-contain display-block mx-auto p-1"
								/>
								<Button
									type="button"
									variant="ghost"
									size="icon"
									onClick={removeScreenshot}
									aria-label="Remove screenshot"
									className="absolute top-1 right-1 h-6 w-6 bg-black/60 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80">
									<X className="h-4 w-4" />
								</Button>
							</div>
						) : (
							<Button
								type="button"
								variant="outline"
								onClick={() => fileInputRef.current?.click()}
								className="w-full flex items-center justify-center gap-2 text-sm py-2 border-dashed hover:border-solid transition-all"
								style={{
									color: config.textColor || '#374151',
									borderColor: config.primaryColor + '60',
									backgroundColor: isGlassmorphismEnabled()
										? 'rgba(255, 255, 255, 0.1)'
										: 'transparent',
								}}>
								<ImageIcon
									className="h-4 w-4"
									style={{
										color: config.primaryColor || '#6B7280',
							}}
						/>
						{t('attach_screenshot_btn')}
					</Button>
						)}
						<input
							type="file"
							ref={fileInputRef}
							hidden
							accept="image/png, image/jpeg"
							onChange={handleFileChange}
						/>
					</div>

					{/* ---- START: Add Reporter Info ---- */}
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
				<div className="space-y-1.5">
					<Label
						htmlFor="contact-sender-name"
						className="text-sm font-medium"
						style={{ color: config.textColor }}>
						{t('your_name_optional')}
					</Label>
							<Input
								id="contact-sender-name"
								placeholder="Jane Doe"
								value={contactSenderName}
								onChange={(e) => setContactSenderName(e.target.value)}
								className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700"
								style={{
									color: '#333',
									backgroundColor: config.glassmorphism?.enabled
										? 'rgba(255, 255, 255, 0.95)'
										: '#FFFFFF',
								}}
							/>
						</div>
				<div className="space-y-1.5">
					<Label
						htmlFor="contact-sender-email"
						className="text-sm font-medium"
						style={{ color: config.textColor }}>
						{t('your_email_optional')}
					</Label>
							<Input
								id="contact-sender-email"
								type="email"
								placeholder="jane@example.com"
								value={contactSenderEmail}
								onChange={(e) => setContactSenderEmail(e.target.value)}
								className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700"
								style={{
									color: '#333',
									backgroundColor: config.glassmorphism?.enabled
										? 'rgba(255, 255, 255, 0.95)'
										: '#FFFFFF',
								}}
							/>
						</div>
				</div>
				<p className="text-xs" style={{ color: config.textColor + 'b3' }}>
					{t('providing_email_help')}
				</p>
					{/* ---- END: Add Reporter Info ---- */}

					{contactSubmitError && (
						<p className="text-sm text-red-600 font-medium">{contactSubmitError}</p>
					)}
					<Button
						type="button"
						onClick={handleContactSubmit}
						disabled={isSubmittingContact || contactMessage.trim().length < 10}
						className="w-full py-2.5 text-sm font-semibold"
						style={{
							backgroundColor: config.primaryColor,
							color: 'white',
							opacity:
								isSubmittingContact || contactMessage.trim().length < 10 ? 0.6 : 1,
						}}>
						{isSubmittingContact && (
							<Loader2 className="h-4 w-4 mr-2 animate-spin" />
						)}{' '}
						Send Message
					</Button>
				</div>
			</div>
		)
	}

	const renderFeatureList = (): React.ReactNode => {
		const getStatusColor = (status: string | undefined | null) => {
			// Add null safety check
			const safeStatus = (status || 'new').toLowerCase()

			// Check if we're using a dark theme
			const isDarkTheme =
				config.backgroundColor === '#111827' ||
				config.backgroundColor?.includes('rgb(17, 24, 39)')

			if (isDarkTheme) {
				// Dark theme status colors
				switch (safeStatus) {
					case 'new':
						return {
							bg: 'rgba(251, 191, 36, 0.2)',
							text: '#FCD34D',
							border: 'rgba(251, 191, 36, 0.3)',
						}
					case 'considering':
						return {
							bg: 'rgba(251, 191, 36, 0.2)',
							text: '#FCD34D',
							border: 'rgba(251, 191, 36, 0.3)',
						}
					case 'planned':
						return {
							bg: 'rgba(99, 102, 241, 0.2)',
							text: '#818CF8',
							border: 'rgba(99, 102, 241, 0.3)',
						}
					case 'in_progress':
						return {
							bg: 'rgba(99, 102, 241, 0.2)',
							text: '#818CF8',
							border: 'rgba(99, 102, 241, 0.3)',
						}
					case 'launched':
						return {
							bg: 'rgba(34, 197, 94, 0.2)',
							text: '#4ADE80',
							border: 'rgba(34, 197, 94, 0.3)',
						}
					case 'completed':
						return {
							bg: 'rgba(34, 197, 94, 0.2)',
							text: '#4ADE80',
							border: 'rgba(34, 197, 94, 0.3)',
						}
					default:
						return {
							bg: 'rgba(107, 114, 128, 0.2)',
							text: '#9CA3AF',
							border: 'rgba(107, 114, 128, 0.3)',
						}
				}
			} else {
				// Light theme status colors (original)
				switch (safeStatus) {
					case 'new':
						return { bg: '#FFF4CC', text: '#B58707', border: '#FFECAA' }
					case 'considering':
						return { bg: '#FFF4CC', text: '#B58707', border: '#FFECAA' }
					case 'planned':
						return { bg: '#E0EAFF', text: '#3B66FF', border: '#C7D9FF' }
					case 'in_progress':
						return { bg: '#E0EAFF', text: '#3B66FF', border: '#C7D9FF' }
					case 'launched':
						return { bg: '#E3FFF1', text: '#0E884D', border: '#C1F4D2' }
					case 'completed':
						return { bg: '#E3FFF1', text: '#0E884D', border: '#C1F4D2' }
					default:
						return { bg: '#F3F4F6', text: '#6B7280', border: '#E5E7EB' }
				}
			}
		}

		// Filter by search query with comprehensive null checks
		const filteredFeatures = featureRequests.filter((feature) => {
			// Skip completely invalid features
			if (!feature || typeof feature !== 'object') {
				return false
			}

			// Skip features with missing essential properties
			if (!feature.id) {
				return false
			}

			// Add defensive checks for null/undefined values
			const title = feature.title || ''
			const description = feature.description || ''
			const searchQuery = featureSearchQuery.toLowerCase()

			const matches =
				title.toLowerCase().includes(searchQuery) ||
				description.toLowerCase().includes(searchQuery)

			return matches
		})

		// Sort by upvote count (highest first)
		const sortedFeatures = [...filteredFeatures].sort(
			(a, b) => b.upvote_count - a.upvote_count
		)

		// Get top 2 and remaining features
		const topFeatures = sortedFeatures.slice(0, 2)
		const remainingFeatures = sortedFeatures.slice(2)

		// Features to display based on showAllFeatures state
		const displayedFeatures = showAllFeatures ? sortedFeatures : topFeatures

		const FeatureCard = ({ feature }: { feature: FeatureRequest }) => {
			const statusColors = getStatusColor(feature.status)
			const isUpvoted = upvotedFeatures[feature.id] || false
			const isUpvoting = upvotingFeature === feature.id

			// Add defensive check for invalid feature ID
			const hasValidId =
				feature &&
				feature.id &&
				typeof feature.id === 'string' &&
				feature.id !== 'undefined'

			return (
				<div
					className="p-4 transition-colors border-b last:border-b-0"
					style={{
						backgroundColor: config.glassmorphism?.enabled
							? 'transparent'
							: config.backgroundColor,
						color: config.textColor,
						borderColor: getBorderColor(),
					}}>
					<div className="flex items-start gap-4">
						{/* Upvote button */}
						<div className="mt-1">
							<button
								type="button"
								className={`flex flex-col items-center justify-center p-1 rounded-md transition-all ${
									isUpvoted || !hasValidId ? '' : 'hover:opacity-80'
								}`}
								style={{
									width: '40px',
									height: '56px',
									boxShadow: isUpvoted ? 'none' : '0 1px 2px rgba(0,0,0,0.05)',
									border: `1px solid ${
										isUpvoted
											? config.primaryColor
											: config.glassmorphism?.enabled
											? config.textColor + '30'
											: config.backgroundColor === '#111827'
											? '#374151'
											: '#E5E7EB'
									}`,
									backgroundColor: isUpvoted
										? config.primaryColor
										: config.glassmorphism?.enabled
										? 'rgba(255, 255, 255, 0.1)'
										: config.backgroundColor === '#111827'
										? '#1F2937'
										: 'white',
									opacity: !hasValidId ? 0.5 : 1,
								}}
								onClick={() =>
									hasValidId && !isUpvoted && handleUpvote(feature.id)
								}
								disabled={!hasValidId || isUpvoted || isUpvoting}>
								{isUpvoting ? (
									<Loader2 className="h-4 w-4 animate-spin mb-1" />
								) : (
									<ChevronUp
										className="h-4 w-4"
										style={{
											color: isUpvoted
												? 'white'
												: config.backgroundColor === '#111827'
												? '#D1D5DB'
												: '#374151',
										}}
									/>
								)}
								<span
									className="text-sm font-medium"
									style={{
										color: isUpvoted
											? 'white'
											: config.glassmorphism?.enabled
											? config.textColor
											: config.backgroundColor === '#111827'
											? '#D1D5DB'
											: '#374151',
									}}>
									{feature.upvote_count || 0}
								</span>
							</button>
						</div>

						{/* Content */}
						<div className="flex-1">
							<div className="flex items-center gap-2 mb-1">
								<h3
									className="text-base font-medium"
									style={{ color: config.textColor }}>
									{feature.title || 'Untitled Feature'}
								</h3>
								<span
									className="text-xs px-2 py-0.5 rounded-full font-medium"
									style={{
										backgroundColor: statusColors.bg,
										color: statusColors.text,
										border: `1px solid ${statusColors.border}`,
									}}>
									{feature.status || 'New'}
								</span>
								{!hasValidId && (
									<span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-600 border border-red-200">
										Invalid ID
									</span>
								)}
							</div>
							<p
								className="text-sm mb-1"
								style={{ color: config.textColor, opacity: 0.8 }}>
								{feature.description || 'No description provided'}
							</p>
							<div
								className="flex items-center text-xs mt-1.5"
								style={{ color: config.textColor, opacity: 0.6 }}>
								<Clock className="h-3 w-3 mr-1" />
								<span>
									{feature.created_at
										? new Date(feature.created_at).toLocaleDateString(
												undefined,
												{
													month: 'short',
													day: 'numeric',
												}
										  )
										: 'Unknown date'}
								</span>
							</div>
						</div>
					</div>
				</div>
			)
		}

		return (
			<div className="flex flex-col h-full">
				{/* Header with search - stays fixed */}
				<div
					className="p-4 border-b"
					style={{
						backgroundColor: getTransparentBackgroundColor(),
						borderColor: getBorderColor(),
					}}>
					<div className="relative mb-4">
						<Search
							className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4"
							style={{
								color: isGlassmorphismEnabled()
									? config.textColor + '80'
									: '#9CA3AF',
							}}
						/>
					<Input
						type="search"
						placeholder={t('search_feature_requests')}
						value={featureSearchQuery}
						onChange={(e) => setFeatureSearchQuery(e.target.value)}
							className="w-full pl-9 py-2 border focus-visible:ring-2"
							style={
								{
									backgroundColor: getInputBackgroundColor(),
									borderColor: getBorderColor(),
									color: '#333',
									'--tw-ring-color': config.primaryColor,
								} as React.CSSProperties
							}
						/>
					</div>
					<Button
						type="button"
						onClick={() => navigateTo('featureForm')}
						className="w-full flex items-center justify-center gap-2"
						style={{
							backgroundColor: config.primaryColor,
							color: 'white',
					}}>
					<Plus className="h-4 w-4" />
					<span>{t('suggest_feature')}</span>
				</Button>
				</div>

				{/* Scrollable content area */}
				<div className="flex-1 overflow-hidden">
					{isLoadingFeatures ? (
						<div className="flex flex-col items-center justify-center p-12 text-center">
							<Loader2
								className="h-10 w-10 animate-spin mb-3"
								style={{ color: config.primaryColor }}
							/>
							<p style={{ color: config.textColor + 'b3' }}>
								Loading feature requests...
							</p>
						</div>
					) : featureLoadError ? (
						<div className="flex flex-col items-center justify-center p-12 text-center">
							<AlertCircle className="h-10 w-10 text-red-500 mb-3" />
							<p className="text-red-600 font-medium mb-1">
								Unable to load feature requests
							</p>
							<p className="text-sm" style={{ color: config.textColor + 'b3' }}>
								{featureLoadError}
							</p>
						</div>
					) : filteredFeatures.length === 0 ? (
						<div
							className="flex flex-col items-center justify-center p-12 text-center"
							style={{
								backgroundColor: isGlassmorphismEnabled()
									? 'rgba(255, 255, 255, 0.02)'
									: config.backgroundColor,
							}}>
							{featureSearchQuery ? (
								<>
									<Search
										className="h-10 w-10 mb-3"
										style={{ color: config.textColor + '60' }}
									/>
									<p
										className="font-medium mb-1"
										style={{ color: config.textColor }}>
										No matching features
									</p>
									<p style={{ color: config.textColor + 'b3' }}>
										Try a different search term
									</p>
								</>
							) : (
								<>
									<LightbulbIcon
										className="h-10 w-10 mb-3"
										style={{
											color: config.menuItemColors?.feature_icon || '#059669',
										}}
									/>
									<p
										className="font-medium mb-1"
									style={{ color: config.textColor }}>
									{t('no_feature_requests')}
								</p>
								<p
									className="mb-4"
									style={{ color: config.textColor + 'b3' }}>
									{t('be_first_suggest')}
								</p>
									<Button
										type="button"
										size="sm"
										onClick={() => navigateTo('featureForm')}
										style={{
											backgroundColor: config.primaryColor,
											color: 'white',
										}}>
									{t('suggest_a_feature')}
								</Button>
								</>
							)}
						</div>
					) : (
						<div
							className="overflow-y-auto h-full"
							style={{ maxHeight: 'calc(60vh - 50px)', minHeight: '250px' }}>
							{/* Top Features Section */}
							{!showAllFeatures && topFeatures.length > 0 && (
								<div
									className="border-b pt-3"
									style={{
										backgroundColor: `${config.primaryColor}1A`,
										borderColor: `${config.primaryColor}33`,
									}}>
									<h4
										className="text-xs font-semibold uppercase px-4 mb-2"
										style={{ color: config.primaryColor }}>
										Top Feature Requests
									</h4>
									{topFeatures.map((feature) => (
										<FeatureCard key={feature.id} feature={feature} />
									))}
								</div>
							)}

							{/* Show More Button (only if there are more features) */}
							{!showAllFeatures && remainingFeatures.length > 0 && (
								<div className="p-3 text-center">
									<Button
										type="button"
										variant="ghost"
										onClick={() => setShowAllFeatures(true)}
										className="text-sm font-medium"
										style={{ color: config.primaryColor }}>
										Show All Feature Requests ({filteredFeatures.length})
									</Button>
								</div>
							)}

							{/* All Features (when expanded) */}
							{showAllFeatures && (
								<>
									{displayedFeatures.map((feature) => (
										<FeatureCard key={feature.id} feature={feature} />
									))}

									{/* Show Less Button */}
									{filteredFeatures.length > 2 && (
										<div className="p-3 text-center">
											<Button
												type="button"
												variant="ghost"
												onClick={() => setShowAllFeatures(false)}
												className="text-sm font-medium"
												style={{ color: config.primaryColor }}>
												Show Top Features
											</Button>
										</div>
									)}
								</>
							)}
						</div>
					)}
				</div>
			</div>
		)
	}

	const renderFeatureForm = (): React.ReactNode => {
		// Categories with proper UUID format
		const categories = [
			{ id: '3fa85f64-5717-4562-b3fc-2c963f66afa6', name: 'UI/UX' },
			{ id: '41ef62c8-7560-4d6b-b4db-f143c0cc3930', name: 'Performance' },
			{ id: 'b3f28105-cc39-45c0-a751-cb0516208aa0', name: 'Integrations' },
			{ id: '9e15d138-3919-4de6-9696-29902cd537b1', name: 'Security' },
		]

		const handleSubmit = () => {
			// Validation is handled in handleFeatureSubmit
			handleFeatureSubmit()
		}

		return (
			<div className="p-4">
			<h3
				className="text-lg font-medium mb-4"
				style={{ color: config.textColor }}>
				{t('suggest_feature')}
			</h3>

				<div className="space-y-4">
					<div>
					<Label
						htmlFor="title"
						className="text-sm font-medium"
						style={{ color: config.textColor }}>
						{t('title')}
					</Label>
					<Input
						id="title"
						value={newFeatureTitle}
						onChange={(e) => setNewFeatureTitle(e.target.value)}
						placeholder={t('feature_idea_brief')}
							className="mt-1"
							style={getFormFieldStyle()}
							required
						/>
					</div>

				<div>
					<Label
						htmlFor="description"
						className="text-sm font-medium"
						style={{ color: config.textColor }}>
						{t('description')}
					</Label>
					<Textarea
						id="description"
						value={newFeatureDescription}
						onChange={(e) => setNewFeatureDescription(e.target.value)}
						placeholder={t('feature_idea_detail')}
							className="mt-1"
							rows={4}
							style={getFormFieldStyle()}
							required
						/>
					</div>

					{/* ---- START: Add Submitter Info ---- */}
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="space-y-1.5">
						<Label
							htmlFor="feature-submitter-name"
							className="text-sm font-medium"
							style={{ color: config.textColor }}>
							{t('your_name_optional')}
						</Label>
							<Input
								id="feature-submitter-name"
								placeholder="John Smith"
								value={submitterName}
								onChange={(e) => setSubmitterName(e.target.value)}
								className="mt-1"
								style={getFormFieldStyle()}
							/>
						</div>
					<div className="space-y-1.5">
						<Label
							htmlFor="feature-submitter-email"
							className="text-sm font-medium"
							style={{ color: config.textColor }}>
							{t('your_email_optional')}
						</Label>
							<Input
								id="feature-submitter-email"
								type="email"
								placeholder="john@example.com"
								value={submitterEmail}
								onChange={(e) => setSubmitterEmail(e.target.value)}
								className="mt-1"
								style={getFormFieldStyle()}
							/>
						</div>
					</div>
					<p className="text-xs" style={{ color: config.textColor + 'b3' }}>
						We might email you for clarification or updates on your suggestion.
					</p>
					{/* ---- END: Add Submitter Info ---- */}

					<Button
						type="button"
						onClick={handleSubmit}
						className="w-full"
						disabled={
							!newFeatureTitle || !newFeatureDescription || isSubmittingFeature
						}
						style={{
							backgroundColor: config.primaryColor,
							color: 'white',
							opacity:
								!newFeatureTitle ||
								!newFeatureDescription ||
								isSubmittingFeature
									? 0.5
									: 1,
						}}>
						{isSubmittingFeature && (
							<Loader2 className="h-4 w-4 mr-2 animate-spin" />
						)}
						Submit Feature Request
					</Button>
					{featureSubmitError && (
						<p className="text-sm text-red-600 font-medium">
							{featureSubmitError}
						</p>
					)}
				</div>
			</div>
		)
	}

	const renderThankYou = (): React.ReactNode => {
		// Apply theme-aware spacing
		const containerPadding =
			config.layout?.spacing === 'compact'
				? 'px-4 py-6'
				: config.layout?.spacing === 'spacious'
				? 'px-8 py-12'
				: 'px-6 py-10'

		// Use theme primary color instead of hardcoded green
		const iconColor = config.primaryColor || '#0066FF'
		const iconBgColor = isGlassmorphismEnabled()
			? iconColor + '20'
			: iconColor + '15'

		return (
			<div
				className={`text-center ${containerPadding} flex flex-col items-center justify-center relative`}
				style={{
					background: isGlassmorphismEnabled()
						? 'rgba(255, 255, 255, 0.02)'
						: 'transparent',
				}}>
				{/* Success Icon with theme colors */}
				<div
					className={`h-16 w-16 mx-auto mb-6 rounded-full flex items-center justify-center ${
						isGlassmorphismEnabled() ? 'backdrop-blur-sm border' : ''
					}`}
					style={{
						backgroundColor: iconBgColor,
						borderColor: isGlassmorphismEnabled()
							? iconColor + '30'
							: 'transparent',
					}}>
					<Check className="h-8 w-8" style={{ color: iconColor }} />
				</div>

				{/* Title with theme text color */}
				<h3
					className="text-xl font-semibold mb-3"
					style={{
						color: config.layout?.headerTextColor || config.textColor || '#111',
					}}>
					{config.thankYou?.title || "We've received your submission!"}
				</h3>

				{/* Message with theme text color and opacity */}
				<p
					className="text-sm mb-8 max-w-sm leading-relaxed"
					style={{
						color: (config.textColor || '#111') + 'cc',
					}}>
					{config.thankYou?.message || 'Thanks for helping us improve.'}
				</p>

				{/* Done button with theme styling */}
				<Button
					type="button"
						onClick={() => resetWidget()}
					className={`text-sm font-medium py-2.5 px-6 transition-all duration-200 ${
						isGlassmorphismEnabled() ? 'backdrop-blur-sm border' : ''
					}`}
					style={{
						backgroundColor: config.primaryColor || '#0066FF',
						color: 'white',
						borderColor: isGlassmorphismEnabled()
							? (config.primaryColor || '#0066FF') + '50'
							: 'transparent',
					}}>
					Done
				</Button>
			</div>
		)
	}

	// Dedicated submission handler for the simple review form
	const handleReviewSubmit = async (): Promise<void> => {
		if (!reviewRating) {
			setReviewSubmitError('Please select a rating.')
			return
		}
		if (reviewText.trim().length < 10) {
			setReviewSubmitError('Please provide some details (min 10 chars).')
			return
		}
		if (!reviewConsent) {
			setReviewSubmitError('Please agree to the consent checkbox.')
			return
		}
		if (!config.id || !config.team_id || !config.program_id) {
			setReviewSubmitError('Config incomplete.')
			return
		}

		setIsSubmittingReview(true)
		setReviewSubmitError(null)

		try {
			// Enhanced review data structure
			const reviewData = {
				reviewText: reviewText,
				consentGiven: reviewConsent,
				customAnswers: {
					rating: {
						questionText: 'Overall Rating',
						answer: String(reviewRating),
						type: 'scale',
					},
					review: {
						questionText: 'Review Text',
						answer: reviewText,
						type: 'text',
					},
				},
			}

			// Submit using service
			await submitReview({
				widgetId: config.id,
				teamId: config.team_id,
				programId: config.program_id,
				score: reviewRating,
				reviewText: reviewText,
				responseData: reviewData,
				consentGiven: reviewConsent,
				reporterEmail: reviewEmail || undefined,
				reporterName: reviewName || undefined,
			})

			setLastSubmissionType('review')
			setViewMode('thankYou')
		} catch (error) {
			setReviewSubmitError('Failed to submit review. Please try again.')
		} finally {
			setIsSubmittingReview(false)
		}
	}

	// Dedicated rendering function for the simple review form
	const renderReviewForm = (): React.ReactNode => {
		return (
			<div className="px-4 pb-4 pt-2 space-y-4">
			<h3
				className="text-center font-semibold text-base"
				style={{ color: config.textColor }}>
				{t('reviews')}
			</h3>

				{verificationStep === 'form' ? (
					<>
						<div className="space-y-1">
						<Label
							className="text-sm font-medium"
							style={{ color: config.textColor }}>
							{t('overall_rating')}
						</Label>
							{/* Star rating with hover preview */}
							<div className="flex justify-between items-center px-2 mb-4">
								{[1, 2, 3, 4, 5].map((value) => (
									<Star
										key={value}
										size={36}
										className={cn(
											'cursor-pointer transition-all duration-150 ease-in-out',
											(hoverRating ?? reviewRating ?? 0) >= value
												? 'text-yellow-400 fill-yellow-400 scale-110'
												: 'text-gray-300 hover:text-yellow-300 hover:scale-105'
										)}
										onMouseEnter={() => setHoverRating(value)}
										onMouseLeave={() => setHoverRating(null)}
										onClick={() => setReviewRating(value)}
									/>
								))}
							</div>
						</div>

						<div className="space-y-1">
						<Label
							htmlFor="review-text"
							className="text-sm font-medium"
							style={{ color: config.textColor }}>
							{t('share_experience')}
						</Label>
							<Textarea
								id="review-text"
								value={reviewText}
								onChange={(e) => setReviewText(e.target.value)}
								rows={3}
								required
								minLength={10}
								className="bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700 rounded-md p-2 w-full text-sm focus:ring-1 focus:ring-opacity-50 focus:border-transparent"
								style={
									{
										color: '#333',
										borderColor: config.primaryColor,
										'--tw-ring-color': config.primaryColor,
							} as React.CSSProperties
						}
						placeholder={t('tell_us_what_you_think')}
					/>
						</div>

						{/* Email field (required) */}
						<div className="space-y-1">
							<Label
								htmlFor="review-email"
							className="text-sm font-medium"
							style={{ color: config.textColor }}>
							{t('email_required_verification')}
						</Label>
							<Input
								id="review-email"
								type="email"
								value={reviewEmail}
								onChange={(e) => setReviewEmail(e.target.value)}
								className="bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700 rounded-md p-2 w-full text-sm focus:ring-1 focus:ring-opacity-50 focus:border-transparent"
								style={
									{
										color: '#333',
										borderColor: config.primaryColor,
										'--tw-ring-color': config.primaryColor,
						} as React.CSSProperties
					}
					placeholder={t('email_placeholder')}
					required
				/>
							<p
								className="text-xs italic"
								style={{ color: config.textColor + '99' }}>
								Your email won't be displayed publicly
							</p>
						</div>

						{/* Name field (optional) */}
						<div className="space-y-1">
							<Label
								htmlFor="review-name"
							className="text-sm font-medium"
							style={{ color: config.textColor }}>
							{t('name_optional')}
						</Label>
							<Input
								id="review-name"
								type="text"
								value={reviewName}
								onChange={(e) => setReviewName(e.target.value)}
								className="bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700 rounded-md p-2 w-full text-sm focus:ring-1 focus:ring-opacity-50 focus:border-transparent"
								style={
									{
										color: '#333',
										borderColor: config.primaryColor,
										'--tw-ring-color': config.primaryColor,
									} as React.CSSProperties
								}
								placeholder={t('full_name_or_company')}
							/>
						</div>

						{/* Social Media Link field (optional) */}
						<div className="space-y-1">
						<Label
							htmlFor="review-social"
							className="text-sm font-medium"
							style={{ color: config.textColor }}>
							{t('social_media_link')}
						</Label>
							<Input
								id="review-social"
								type="url"
								value={reviewSocialLink}
								onChange={(e) => setReviewSocialLink(e.target.value)}
								className="bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700 rounded-md p-2 w-full text-sm focus:ring-1 focus:ring-opacity-50 focus:border-transparent"
								style={
									{
										color: '#333',
										borderColor: config.primaryColor,
										'--tw-ring-color': config.primaryColor,
									} as React.CSSProperties
								}
								placeholder="https://twitter.com/yourusername"
							/>
						</div>

						<div className="flex items-start space-x-2">
							<input
								type="checkbox"
								id="review-consent"
								checked={reviewConsent}
								onChange={(e) => setReviewConsent(e.target.checked)}
								className="mt-1 form-checkbox h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
								style={{ accentColor: config.primaryColor }}
								required
							/>
							<Label
								htmlFor="review-consent"
						className="text-xs font-normal leading-snug select-none cursor-pointer"
						style={{ color: config.textColor + 'cc' }}>
						{t('i_agree_feedback')}
					</Label>
						</div>

						{verificationError && (
							<p className="text-sm text-red-600 font-medium">
								{verificationError}
							</p>
						)}

						<div className="flex flex-col space-y-2">
							<Button
								type="button"
								onClick={() => handleRequestVerificationCode(false)}
								disabled={
									isRequestingCode ||
									!reviewEmail ||
									!reviewRating ||
									reviewText.trim().length < 10 ||
									!reviewConsent
								}
								className="w-full py-2.5 mt-2"
								style={{
									backgroundColor: config.primaryColor,
									color: 'white',
									opacity:
										isRequestingCode ||
										!reviewEmail ||
										!reviewRating ||
										reviewText.trim().length < 10 ||
										!reviewConsent
											? 0.6
											: 1,
								}}>
							{isRequestingCode ? (
								<Loader2 className="h-4 w-4 mr-2 animate-spin" />
							) : null}
							{t('continue')}
						</Button>

							{canResendCode && (
								<Button
									type="button"
									variant="outline"
									onClick={() => handleRequestVerificationCode(true)}
									disabled={isRequestingCode}
									className="w-full">
									{isRequestingCode ? (
										<Loader2 className="h-4 w-4 mr-2 animate-spin" />
									) : null}
									Resend Verification Code
								</Button>
							)}
						</div>
					</>
				) : verificationStep === 'verification' ? (
					<>
						<div className="text-center mb-4">
							<p style={{ color: config.textColor }}>
								We've sent a verification code to:
							</p>
							<p className="font-medium" style={{ color: config.textColor }}>
								{reviewEmail}
							</p>
							<p
								className="text-sm mt-2"
								style={{ color: config.textColor + 'cc' }}>
								Please enter the 6-digit code below to verify your review.
							</p>
						</div>

						<div className="space-y-1">
							<Label
								htmlFor="verification-code"
								className="text-sm font-medium"
								style={{ color: config.textColor }}>
								Verification Code
							</Label>
							<Input
								id="verification-code"
								type="text"
								value={verificationCode}
								onChange={(e) =>
									setVerificationCode(
										e.target.value.replace(/[^0-9]/g, '').slice(0, 6)
									)
								}
								className="bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700 rounded-md text-center p-2 w-full text-lg font-mono tracking-wider focus:ring-1 focus:ring-opacity-50 focus:border-transparent"
								style={
									{
										color: '#333',
										borderColor: config.primaryColor,
										'--tw-ring-color': config.primaryColor,
									} as React.CSSProperties
								}
								placeholder="123456"
								maxLength={6}
								required
							/>
						</div>

						{verificationError && (
							<p className="text-sm text-red-600 font-medium">
								{verificationError}
							</p>
						)}

						<div className="flex flex-col space-y-2">
							<Button
								type="button"
								onClick={handleVerifyAndSubmitReview}
								disabled={
									isVerifyingCode ||
									!verificationCode ||
									verificationCode.length !== 6
								}
								className="w-full py-2.5"
								style={{
									backgroundColor: config.primaryColor,
									color: 'white',
									opacity:
										isVerifyingCode ||
										!verificationCode ||
										verificationCode.length !== 6
											? 0.6
											: 1,
								}}>
								{isVerifyingCode ? (
									<Loader2 className="h-4 w-4 mr-2 animate-spin" />
								) : null}
								Verify & Submit Review
							</Button>

							<div className="flex justify-between">
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={() => setVerificationStep('form')}
									disabled={isVerifyingCode || isRequestingCode}
									className="text-xs">
									<ArrowLeft className="h-3 w-3 mr-1" />
									Change Email
								</Button>

								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={() => handleRequestVerificationCode(true)}
									disabled={isVerifyingCode || isRequestingCode}
									className="text-xs">
									Resend Code
									{isRequestingCode && (
										<Loader2 className="h-3 w-3 ml-1 animate-spin" />
									)}
								</Button>
							</div>
						</div>
					</>
				) : null}
			</div>
		)
	}

	// Request verification code for review
	const handleRequestVerificationCode = async (
		resend: boolean = false
	): Promise<void> => {
		if (!reviewEmail) {
			setVerificationError('Please enter your email address.')
			return
		}
		if (!reviewRating) {
			setVerificationError('Please select a rating.')
			return
		}
		if (reviewText.trim().length < 10) {
			setVerificationError('Please provide some details (min 10 chars).')
			return
		}
		if (!reviewConsent) {
			setVerificationError('Please agree to the consent checkbox.')
			return
		}
		if (!config.id || !config.program_id) {
			setVerificationError('Config incomplete.')
			return
		}

		setIsRequestingCode(true)
		setVerificationError(null)

		try {
			const result = await requestVerificationCode(
				{
					widgetId: config.id,
					programId: config.program_id,
					email: reviewEmail,
					reviewText: reviewText,
					score: reviewRating,
					name: reviewName,
					socialLink: reviewSocialLink,
				},
				resend
			)

			if (result.success) {
				// If successful, move to verification step
				setVerificationStep('verification')
				setCanResendCode(false)
			} else if (result.canResend) {
				// If code was already sent but we can resend
				setCanResendCode(true)
				setVerificationError(
					'A verification code was already sent to your email. You can use that code or request a new one.'
				)
			}
		} catch (error) {
			let errorMessage = 'Failed to send verification code. Please try again.'

			// Parse error message if available
			if (error instanceof Error) {
				const match = error.message.match(/API Error: \d+ .+ - (.+) \((.+)\)/)
				if (match) {
					errorMessage = match[1]

					// Special handling for specific error codes
					if (match[2] === 'email_already_verified') {
						errorMessage =
							'You have already submitted a verified review for this program.'
					}
				}
			}

			setVerificationError(errorMessage)
		} finally {
			setIsRequestingCode(false)
		}
	}

	// Submit verified review with code
	const handleVerifyAndSubmitReview = async (): Promise<void> => {
		if (!verificationCode || verificationCode.length !== 6) {
			setVerificationError('Please enter the 6-digit verification code.')
			return
		}
		if (!config.id || !config.program_id) {
			setVerificationError('Config incomplete.')
			return
		}

		setIsVerifyingCode(true)
		setVerificationError(null)

		try {
			await submitVerifiedReview({
				widgetId: config.id,
				programId: config.program_id,
				email: reviewEmail,
				verificationCode,
				score: reviewRating,
				reviewText: reviewText,
				consentGiven: reviewConsent,
				name: reviewName || undefined,
				socialLink: reviewSocialLink || undefined,
			})

			setLastSubmissionType('review')
			setViewMode('thankYou')
		} catch (error) {
			let errorMessage = 'Failed to verify your review. Please try again.'

			// Parse error message if available
			if (error instanceof Error) {
				const match = error.message.match(/API Error: \d+ .+ - (.+) \((.+)\)/)
				if (match) {
					errorMessage = match[1]

					// Special handling for specific error codes
					if (match[2] === 'invalid_code') {
						errorMessage = 'Invalid verification code. Please try again.'
					} else if (match[2] === 'code_expired') {
						errorMessage =
							'Verification code has expired. Please request a new one.'
						setVerificationStep('form') // Go back to request form
					}
				}
			}

			setVerificationError(errorMessage)
		} finally {
			setIsVerifyingCode(false)
		}
	}

	// ===== Live Chat Functions =====
	const generateEndUserIdentifier = (): string => {
		// Check if we already have an identifier in localStorage
		const storageKey = `feedbask_chat_id_${config.id}`
		const existingId = localStorage.getItem(storageKey)

		// Validate existing ID is a proper UUID
		const uuidRegex =
			/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
		if (existingId && uuidRegex.test(existingId)) {
			return existingId
		}

		// Clear invalid identifier if exists
		if (existingId) {
			localStorage.removeItem(storageKey)
		}

		// Generate a proper UUID v4
		const newId = crypto.randomUUID()
		localStorage.setItem(storageKey, newId)
		return newId
	}

	const renderChatInit = (): React.ReactNode => {
		return (
			<div className="px-4 pb-4 pt-2 space-y-4">
				{/* Display available agents */}
				{!isLoadingAgents && availableAgents.length > 0 && (
					<div className="space-y-3">
						<h3
							className="text-center font-semibold text-base"
							style={{ color: config.textColor }}>
							Chat with our team
						</h3>
						<div className="flex justify-center gap-3 flex-wrap">
							{availableAgents.slice(0, 3).map((agent) => (
								<div
									key={agent.user_id}
									className="flex flex-col items-center space-y-1">
									<div className="relative">
										{agent.avatar_url ? (
											<img
												src={agent.avatar_url}
												alt={agent.full_name}
												className="w-12 h-12 rounded-full object-cover border-2"
												style={{ borderColor: config.primaryColor }}
											/>
										) : (
											<div
												className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold"
												style={{ backgroundColor: config.primaryColor }}>
												{agent.full_name.charAt(0).toUpperCase()}
											</div>
										)}
										<div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
									</div>
									<div className="text-center">
										<p
											className="text-xs font-medium"
											style={{ color: config.textColor }}>
											{agent.full_name.split(' ')[0]}
										</p>
										{agent.job_title && (
											<p
												className="text-xs"
												style={{ color: config.textColor + '99' }}>
												{agent.job_title}
											</p>
										)}
									</div>
								</div>
							))}
						</div>
					</div>
				)}

				{!isLoadingAgents && availableAgents.length === 0 && (
					<>
					<h3
						className="text-center font-semibold text-base"
						style={{ color: config.textColor }}>
						{t('leave_us_message')}
					</h3>
					<p
						className="text-sm text-center"
						style={{ color: config.textColor + 'cc' }}>
						{t('team_offline')}
					</p>
					</>
				)}

				{isLoadingAgents && (
					<>
					<h3
						className="text-center font-semibold text-base"
						style={{ color: config.textColor }}>
						{t('start_conversation')}
					</h3>
						
					</>
				)}

				<div className="space-y-3">
					<div className="space-y-1.5">
				<Label
					htmlFor="chat-name"
					className="text-sm font-medium"
					style={{ color: config.textColor }}>
					{t('chat_name')}
				</Label>
						<Input
							id="chat-name"
							type="text"
							value={chatName}
							onChange={(e) => setChatName(e.target.value)}
							placeholder="John Doe"
							className="bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700 rounded-md p-2 w-full text-sm focus:ring-1 focus:ring-opacity-50 focus:border-transparent"
							style={
								{
									color: '#333',
									borderColor: config.primaryColor,
									'--tw-ring-color': config.primaryColor,
								} as React.CSSProperties
							}
							required
						/>
					</div>

					<div className="space-y-1.5">
					<Label
						htmlFor="chat-email"
						className="text-sm font-medium"
						style={{ color: config.textColor }}>
						{t('work_email')}
					</Label>
						<Input
							id="chat-email"
							type="email"
							value={chatEmail}
							onChange={(e) => setChatEmail(e.target.value)}
							placeholder="john@example.com"
							className="bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700 rounded-md p-2 w-full text-sm focus:ring-1 focus:ring-opacity-50 focus:border-transparent"
							style={
								{
									color: '#333',
									borderColor: config.primaryColor,
									'--tw-ring-color': config.primaryColor,
								} as React.CSSProperties
							}
							required
					/>
					<p className="text-xs text-gray-600 mt-1">
						{t('email_follow_up')}
					</p>
				</div>
				</div>

				{chatError && (
					<p className="text-sm text-red-600 font-medium text-center">
						{chatError}
					</p>
				)}

				<Button
					type="button"
					onClick={handleStartChat}
					disabled={!chatName || !chatEmail || isLoadingChat}
					className="w-full py-2.5"
					style={{
						backgroundColor: config.primaryColor,
						color: 'white',
						opacity: !chatName || !chatEmail || isLoadingChat ? 0.6 : 1,
					}}>
					{isLoadingChat ? (
						<>
							<Loader2 className="h-4 w-4 mr-2 animate-spin inline" />
					Starting Chat...
				</>
			) : (
				t('start_chat')
			)}
		</Button>
			</div>
		)
	}

	const renderChatView = (): React.ReactNode => {
		return (
			<div className="flex flex-col h-full max-h-[500px]">
				{/* Agent Profile Header */}
				{chatAgentProfile && (
					<div className="border-b">
						<AgentProfileCard
							agent={chatAgentProfile}
							compact={true}
							primaryColor={config.primaryColor}
							textColor={config.textColor}
							className="border-0 rounded-none hover:bg-transparent cursor-default"
						/>
					</div>
				)}

				{/* Chat header with end chat option */}
				<div className="flex items-center justify-between px-4 py-2 border-b">
					<div className="flex items-center gap-2">
						{!chatAgentProfile && (
							<>
								<div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
								<span
									className="text-sm font-medium"
									style={{ color: config.textColor }}>
									Live Chat
								</span>
							</>
						)}
						{chatAgentProfile && (
							<span
								className="text-xs"
								style={{ color: config.textColor + '99' }}>
								Chat with {chatAgentProfile.full_name.split(' ')[0]}
							</span>
						)}
					</div>
					<Button
						variant="ghost"
						size="sm"
						onClick={async () => {
							if (confirm('Are you sure you want to end this chat session?')) {
								// End the conversation on the server
								if (chatConversationId) {
									try {
										const backendUrl = getApiBaseUrl()
										const endUserIdentifier = generateEndUserIdentifier()

										const response = await fetch(
											`${backendUrl}/api/public/chat/${chatConversationId}/end`,
											{
												method: 'PATCH',
												headers: {
													'Content-Type': 'application/json',
												},
												body: JSON.stringify({ identifier: endUserIdentifier }),
											}
										)

										if (!response.ok) {
											// Error handling
										}
									} catch (error) {
										// Error handling
									}
								}

								// Clear chat session
								const sessionKey = getChatSessionKey()
								const messagesKey = getChatMessagesKey()
								localStorage.removeItem(sessionKey)
								localStorage.removeItem(messagesKey)

								// Reset chat state
								setChatName('')
								setChatEmail('')
								setChatConversationId(null)
								setChatAgentProfile(null)
								setChatMessages([])
								setChatMessage('')
								setChatError(null)

								// Go back to chat init
								setViewMode('chatInit')
							}
						}}
						className="text-xs"
						style={{ color: config.textColor + '99' }}>
						End Chat
					</Button>
				</div>
				
				{/* History Banner */}
				{chatHistoryLimited && (
					<div className="px-4 py-2 text-xs text-amber-800 bg-amber-50 border-b border-amber-200">
						Showing last {(() => {
							if (!chatHistoryCutoff) return 3
							const ms = Date.now() - new Date(chatHistoryCutoff).getTime()
							return Math.max(1, Math.round(ms / 86400000))
						})()} days of messages
					</div>
				)}

				{/* Chat messages area */}
				<div 
					ref={chatScrollContainerRef}
					onScroll={handleChatScroll}
					className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[200px] max-h-[350px] relative">
					{chatMessages.length === 0 ? (
					<div className="text-center py-8">
						<p className="text-sm" style={{ color: config.textColor + '99' }}>
							{t('say_hi')}
						</p>
					</div>
					) : (
						chatMessages.map((msg) => (
							<div
								key={msg.id}
								className={cn(
									'flex',
									msg.sender_type === 'visitor'
										? 'justify-end'
										: 'justify-start'
								)}>
								<div
									className={cn(
										'max-w-[70%] px-3 py-2 rounded-2xl text-sm',
										msg.sender_type === 'visitor'
											? 'bg-primary text-white rounded-br-md'
											: 'bg-gray-100 text-gray-900 rounded-bl-md'
									)}
									style={
										msg.sender_type === 'visitor'
											? { backgroundColor: config.primaryColor }
											: {}
									}>
									{msg.content}
									{msg.media_url && (
										<div className="relative group mt-2">
											<img
												src={msg.media_url}
												alt="Attachment"
												className="rounded-lg max-w-full cursor-pointer transition-opacity hover:opacity-90"
												onClick={() => {
													setSelectedImageUrl(msg.media_url)
													setIsImageModalOpen(true)
												}}
											/>
											<div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
												<Button
													type="button"
													size="icon"
													variant="secondary"
													className="h-7 w-7 bg-white/90 backdrop-blur-sm shadow-sm hover:bg-white"
													onClick={(e) => {
														e.stopPropagation()
														setSelectedImageUrl(msg.media_url)
														setIsImageModalOpen(true)
													}}>
													<Expand className="h-3.5 w-3.5" />
												</Button>
												<Button
													type="button"
													size="icon"
													variant="secondary"
													className="h-7 w-7 bg-white/90 backdrop-blur-sm shadow-sm hover:bg-white"
													onClick={(e) => {
														e.stopPropagation()
														if (msg.media_url)
															handleDownloadImage(msg.media_url)
													}}>
													<Download className="h-3.5 w-3.5" />
												</Button>
											</div>
										</div>
									)}
								</div>
							</div>
						))
					)}
					{agentTyping && (
						<div className="flex justify-start">
						<div className="max-w-[70%] px-3 py-2 rounded-2xl bg-gray-100 text-gray-900 rounded-bl-md">
							<div className="flex items-center gap-1">
								<span className="text-sm italic">{t('agent_typing')}</span>
								<span className="flex gap-0.5">
										<span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
										<span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
										<span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
									</span>
								</div>
							</div>
						</div>
					)}
					<div ref={chatMessagesEndRef} />
				</div>

				{/* New messages indicator */}
				{hasNewMessages && (
					<div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-10">
						<Button
							type="button"
							size="sm"
							variant="secondary"
							className="rounded-full shadow-lg"
							onClick={() => {
								chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
								setHasNewMessages(false)
								setIsUserScrolledUp(false)
							}}>
							New messages ↓
						</Button>
					</div>
				)}

				{/* Message input area */}
				<div className="border-t p-3">
					<form onSubmit={handleSendMessage} className="flex gap-2">
						<Textarea
							value={chatMessage}
							onChange={(e) => {
								setChatMessage(e.target.value)
								// Send typing indicator
								if (chatConversationId && e.target.value.trim() && presenceChannelRef.current) {
									presenceChannelRef.current.track({
										user_type: 'visitor',
										typing: true,
										timestamp: Date.now()
									})
									// Clear typing after 3 seconds
									if (typingTimeoutRef.current) {
										clearTimeout(typingTimeoutRef.current)
									}
									typingTimeoutRef.current = setTimeout(() => {
										if (presenceChannelRef.current) {
											presenceChannelRef.current.track({
												user_type: 'visitor',
												typing: false,
												timestamp: Date.now()
											})
										}
									}, 3000)
								}
							}}
							onKeyDown={(e) => {
								if (e.key === 'Enter' && !e.shiftKey) {
									e.preventDefault()
									handleSendMessage(e as any)
									// Clear typing indicator when sending
									if (typingTimeoutRef.current) {
										clearTimeout(typingTimeoutRef.current)
									}
									if (presenceChannelRef.current) {
										presenceChannelRef.current.track({
											user_type: 'visitor',
											typing: false,
											timestamp: Date.now()
										})
									}
								}
							}}
							placeholder="Type your message... (Shift+Enter for new line)"
							className="flex-1 bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700 rounded-md p-2 text-sm focus:ring-1 focus:ring-opacity-50 focus:border-transparent min-h-[40px] max-h-[120px] resize-none"
							rows={1}
							style={
								{
									color: '#333',
									borderColor: config.primaryColor + '66',
									'--tw-ring-color': config.primaryColor,
								} as React.CSSProperties
							}
							disabled={isSubmittingChatMessage}
						/>
						<Button
							type="submit"
							disabled={!chatMessage.trim() || isSubmittingChatMessage}
							className="px-3"
							style={{
								backgroundColor: config.primaryColor,
								color: 'white',
								opacity:
									!chatMessage.trim() || isSubmittingChatMessage ? 0.6 : 1,
							}}>
							{isSubmittingChatMessage ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<Send className="h-4 w-4" />
							)}
						</Button>
					</form>
				</div>

				{/* Full-size image modal */}
				{selectedImageUrl && (
					<Dialog open={isImageModalOpen} onOpenChange={setIsImageModalOpen}>
						<DialogContent className="max-w-[90vw] max-h-[90vh] p-0 overflow-hidden">
							<VisuallyHidden>
								<DialogTitle>View Image</DialogTitle>
							</VisuallyHidden>
							<div className="relative w-full h-full max-h-[90vh] overflow-auto bg-background/95">
								<img
									src={selectedImageUrl}
									alt="Full size media"
									className="w-full h-auto object-contain"
								/>
								<div className="absolute top-4 right-4 flex gap-2 z-10">
									<Button
										size="icon"
										variant="secondary"
										className="bg-white/90 backdrop-blur-sm shadow-md hover:bg-white"
										onClick={() => handleDownloadImage(selectedImageUrl)}>
										<Download className="h-4 w-4" />
									</Button>
									<DialogClose asChild>
										<Button
											size="icon"
											variant="secondary"
											className="bg-white/90 backdrop-blur-sm shadow-md hover:bg-white">
											<X className="h-4 w-4" />
										</Button>
									</DialogClose>
								</div>
							</div>
						</DialogContent>
					</Dialog>
				)}
			</div>
		)
	}

	const handleStartChat = async (): Promise<void> => {
		if (!chatName || !chatEmail) {
			setChatError('Please provide your name and email.')
			return
		}
		if (!config.id || !config.team_id) {
			setChatError('Widget configuration incomplete.')
			return
		}

		setIsLoadingChat(true)
		setChatError(null)

		try {
			const backendUrl = getApiBaseUrl()
			const endUserIdentifier = generateEndUserIdentifier()

			// Start a new chat conversation
			const response = await fetch(`${backendUrl}/api/public/chat/start`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					widgetId: config.id,
					name: chatName,
					email: chatEmail,
					identifier: endUserIdentifier,
				}),
			})

			if (!response.ok) {
				const error = await response.json()
				throw new Error(error.error || 'Failed to start chat')
			}

			const data = await response.json()
			setChatConversationId(data.conversation_id)

			// Set agent profile if available
			if (data.agent_profile) {
				setChatAgentProfile(data.agent_profile)
			}

			// Store session in localStorage
			const sessionKey = getChatSessionKey()
			localStorage.setItem(
				sessionKey,
				JSON.stringify({
					conversationId: data.conversation_id,
					name: chatName,
					email: chatEmail,
					timestamp: new Date().toISOString(),
				})
			)

			setViewMode('chatView')

			// Load existing messages if reconnecting
			if (data.messages && data.messages.length > 0) {
				setChatMessages(data.messages)
				// Store messages in localStorage
				const messagesKey = getChatMessagesKey()
				localStorage.setItem(messagesKey, JSON.stringify(data.messages))
			}
			
			if (typeof data.history_limited !== 'undefined') {
				setChatHistoryLimited(!!data.history_limited)
				setChatHistoryCutoff(data.history_cutoff || null)
				const metaKey = getChatMetaKey()
				localStorage.setItem(metaKey, JSON.stringify({ history_limited: !!data.history_limited, history_cutoff: data.history_cutoff || null }))
			}
		} catch (error) {
			setChatError(
				error instanceof Error
					? error.message
					: 'Failed to start chat. Please try again.'
			)
		} finally {
			setIsLoadingChat(false)
		}
	}

	const handleSendMessage = async (e: React.FormEvent): Promise<void> => {
		e.preventDefault()

		if (!chatMessage.trim() || !chatConversationId) return

		const message = chatMessage.trim()
		setChatMessage('')
		setIsSubmittingChatMessage(true)

		// Optimistically add the message to the UI
		const tempMessage = {
			id: 'temp-' + Date.now(),
			sender_type: 'visitor' as const,
			content: message,
			media_url: null,
			created_at: new Date().toISOString(),
			agent_id: null,
		}
		setChatMessages((prev) => [...prev, tempMessage])

		try {
			const backendUrl = getApiBaseUrl()
			const endUserIdentifier = generateEndUserIdentifier()

			const response = await fetch(
				`${backendUrl}/api/public/chat/${chatConversationId}/messages`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						content: message,
						identifier: endUserIdentifier,
					}),
				}
			)

			if (!response.ok) {
				const error = await response.json()
				throw new Error(error.error || 'Failed to send message')
			}

			const data = await response.json()
			// Replace temp message with real one
			setChatMessages((prev) => {
				const updatedMessages = prev.map((msg) =>
					msg.id === tempMessage.id ? data.message : msg
				)
				// Store updated messages in localStorage
				const messagesKey = getChatMessagesKey()
				localStorage.setItem(messagesKey, JSON.stringify(updatedMessages))
				return updatedMessages
			})
		} catch (error) {
			// Remove the optimistic message on error
			setChatMessages((prev) => prev.filter((msg) => msg.id !== tempMessage.id))
			setChatError('Failed to send message. Please try again.')
		} finally {
			setIsSubmittingChatMessage(false)
		}
	}

	// ===== Main Render =====
	return (
		<>
			{/* Floating Action Button */}
			{!isOpen &&
				(config.trigger?.type !== 'manual' || showManualTrigger) &&
				renderFeedbackButton()}

			{/* Main Widget Card */}
			<AnimatePresence>
				{isOpen && (
					<motion.div
						className="z-[2147483001]"
						style={getModalPosition()}
						variants={containerVariants}
						initial="hidden"
						animate="visible"
						exit="exit"
						transformTemplate={({ scale }) => {
							const position = config.position || 'bottom-right'
							const isWallMounted =
								position === 'middle-left' || position === 'middle-right'
							if (isWallMounted) {
								return `translate(-50%, -50%) scale(${scale})`
							}
							return `scale(${scale})`
						}}>
						<Card
							className={cn(
								'w-[400px] shadow-lg rounded-3xl overflow-hidden flex flex-col max-h-[90vh] relative',
								config.layout?.containerBorder === false ? 'border-0' : 'border'
							)}
							style={{
								backgroundColor: config.backgroundColor,
								backdropFilter: isGlassmorphismEnabled()
									? `blur(${config.glassmorphism.backdropBlur || '16px'})`
									: undefined,
								WebkitBackdropFilter: isGlassmorphismEnabled()
									? `blur(${config.glassmorphism.backdropBlur || '16px'})`
									: undefined,
								borderColor: isGlassmorphismEnabled()
									? 'transparent'
									: undefined,
							}}>
							{/* Animated background elements for glassmorphism - now contained within widget */}
							{config.animation?.backgroundEffects && (
								<div className="absolute inset-0 pointer-events-none overflow-hidden rounded-3xl">
									<div
										className="absolute -top-20 -right-20 w-40 h-40 rounded-full blur-2xl animate-pulse opacity-50"
										style={{ backgroundColor: config.primaryColor + '40' }}
									/>
									<div
										className="absolute -bottom-20 -left-20 w-40 h-40 rounded-full blur-2xl animate-pulse opacity-50"
										style={{
											backgroundColor: config.gradientEndColor
												? config.gradientEndColor + '40'
												: config.primaryColor + '40',
											animationDelay: '1s',
										}}
									/>
									<div
										className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full blur-2xl animate-pulse opacity-30"
										style={{
											backgroundColor: config.primaryColor + '30',
											animationDelay: '0.5s',
										}}
									/>
								</div>
							)}

							<WidgetHeader />
							<CardContent className="p-0 flex-grow overflow-y-auto">
								<AnimatePresence mode="wait">
									<motion.div
										key={viewMode}
										variants={contentVariants}
										initial="enter"
										animate="center"
										exit="exit"
										transition={{ duration: 0.2 }}>
										{viewMode === 'mainMenu' && renderMainMenu()}
										{viewMode === 'bugForm' && renderBugForm()}
								{viewMode === 'contactForm' && renderContactForm()}
										{viewMode === 'featureList' && renderFeatureList()}
										{viewMode === 'featureForm' && renderFeatureForm()}
										{viewMode === 'surveyStep' && renderPrimaryFeedbackFlow()}
										{viewMode === 'testimonialForm' &&
											renderPrimaryFeedbackFlow()}
										{viewMode === 'reviewForm' && renderReviewForm()}
										{viewMode === 'thankYou' && renderThankYou()}
										{viewMode === 'chatInit' && renderChatInit()}
										{viewMode === 'chatView' && renderChatView()}
									</motion.div>
								</AnimatePresence>
							</CardContent>
							{renderBottomModuleNavigation()}
							{renderBranding()}
						</Card>
					</motion.div>
				)}
			</AnimatePresence>
		</>
	)
}
